import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  getOrchestrationStatus,
  getHealthStatus,
  triggerScraper,
  getProgress,
  getLocalProgress,
  fetchLocalProgressFromFirestore,
  subscribeLocalServerProgress,
  cancelScraper,
  getLatestReports,
  fetchLocalRunnableScrapers,
  fetchLocalPendingQueue,
  checkLocalServerRunning,
  sendLocalServerCommand,
  startLocalServer,
  subscribeLocalAutomationBatch,
  fetchLatestRunningAutomationBatch,
  getLocalApiBase,
  getScraperDoctorOverview,
  getScraperDoctorReview,
  LocalAutomationBatch,
  LocalPendingQueueItem,
  ScraperStatus,
  HealthStatus,
  ProgressResponse,
  ProgressInfo,
  ScraperReport,
  ScraperDoctorOverview,
  ScraperDoctorReview,
  LocalServerStatus,
  buildEventDashboardLinkState,
} from '../services/scraperOrchestration.ts';
import './ScraperOrchestration.css';

/** Scrapers that only run locally (not on Cloud Run), or that must appear in the table even if the API omits them. */
const LOCAL_ONLY_SCRAPERS: ScraperStatus[] = [
  // Tel Aviv Municipality (tel-aviv.gov.il) — Selenium; local server only (Cloud Run blocks /scrape/tel_aviv_gov)
  {
    name: 'tel_aviv_gov',
    enabled: true,
    schedule_type: 'twice_weekly',
    schedule: { type: 'twice_weekly', days: ['sunday', 'wednesday'], at: '06:00' },
    priority: 1,
    location: 'Tel Aviv, IL',
    trigger_mode: 'scheduled_local',
  },
  // Mugics — nationwide live-music (Selenium + Load More); runs via local_scraper_server only
  { name: 'mugics', enabled: true, schedule_type: 'weekly', schedule: { type: 'weekly', day: 'sunday', at: '06:00' }, priority: 3, location: 'General, IL' },
  // Mevalim + Chillz — nationwide Selenium aggregators (local adapter)
  { name: 'mevalim', enabled: true, schedule_type: 'weekly', schedule: { type: 'weekly', day: 'sunday', at: '09:00' }, priority: 4, location: 'General, IL' },
  { name: 'chillz', enabled: true, schedule_type: 'weekly', schedule: { type: 'weekly', day: 'sunday', at: '09:15' }, priority: 4, location: 'Tel Aviv, IL' },
  // GoShow standup (nationwide) – local-only scraper that doesn't appear in Cloud orchestration API
  { name: 'goshow_standup', enabled: true, schedule_type: 'weekly', schedule: { type: 'weekly', day: 'sunday', at: '08:30' }, priority: 4, location: 'General, IL' },
  { name: 'amama', enabled: true, schedule_type: 'weekly', schedule: { type: 'weekly', day: 'sunday', at: '08:40' }, priority: 4, location: 'Tel Aviv, IL' },
  // Multi-city Facebook Logout (local-only)
  { name: 'facebook_logout_haifa', enabled: true, schedule_type: 'interval_days', schedule: { type: 'interval_days', interval: 2, at: '09:30' }, priority: 1, location: 'Haifa, IL' },
  { name: 'facebook_logout_jerusalem', enabled: true, schedule_type: 'interval_days', schedule: { type: 'interval_days', interval: 2, at: '10:00' }, priority: 1, location: 'Jerusalem, IL' },
  { name: 'facebook_logout_beersheva', enabled: true, schedule_type: 'interval_days', schedule: { type: 'interval_days', interval: 2, at: '10:30' }, priority: 1, location: 'Beer Sheva, IL' },
  { name: 'facebook_logout_ashdod', enabled: true, schedule_type: 'interval_days', schedule: { type: 'interval_days', interval: 2, at: '11:00' }, priority: 1, location: 'Ashdod, IL' },
  { name: 'facebook_logout_rishonlezion', enabled: true, schedule_type: 'interval_days', schedule: { type: 'interval_days', interval: 2, at: '11:30' }, priority: 1, location: 'Rishon LeZion, IL' },
  // Haifa Municipality city events (local-only)
  { name: 'haifamuni', enabled: true, schedule_type: 'weekly', schedule: { type: 'weekly', day: 'sunday', at: '06:45' }, priority: 3, location: 'Haifa, IL' },
  { name: 'haifa_museums', enabled: true, schedule_type: 'weekly', schedule: { type: 'weekly', day: 'sunday', at: '06:50' }, priority: 3, location: 'Haifa, IL' },
  { name: 'beit_hagefen', enabled: true, schedule_type: 'weekly', schedule: { type: 'weekly', day: 'sunday', at: '06:55' }, priority: 3, location: 'Haifa, IL' },
  { name: 'madatech_tickets', enabled: true, schedule_type: 'weekly', schedule: { type: 'weekly', day: 'sunday', at: '07:02' }, priority: 3, location: 'Haifa, IL' },
  { name: 'ethos_haifa', enabled: true, schedule_type: 'weekly', schedule: { type: 'weekly', day: 'sunday', at: '07:05' }, priority: 3, location: 'Haifa, IL' },
  // Aluma Hod — Hod HaSharon community culture (alumahod.com החודש באלומה)
  { name: 'aluma_hod', enabled: true, schedule_type: 'weekly', schedule: { type: 'weekly', day: 'sunday', at: '07:00' }, priority: 3, location: 'Hod HaSharon, IL' },
  // Silo Culture / Siloculture — Hod HaSharon Wix events calendar
  { name: 'siloculture', enabled: true, schedule_type: 'weekly', schedule: { type: 'weekly', day: 'sunday', at: '07:15' }, priority: 3, location: 'Hod HaSharon, IL' },
  // Greenbear / הדוב הירוק — Hod HaSharon park venue
  { name: 'greenbear', enabled: true, schedule_type: 'weekly', schedule: { type: 'weekly', day: 'sunday', at: '07:30' }, priority: 3, location: 'Hod HaSharon, IL' },
  // IBBA / iBasketball — nationwide upcoming basketball games
  { name: 'ibasketball', enabled: true, schedule_type: 'weekly', schedule: { type: 'weekly', day: 'sunday', at: '07:45' }, priority: 2, location: 'Nationwide, IL' },
  // Shvoong — nationwide sports event board (event.shvoong.co.il)
  { name: 'shvoong', enabled: true, schedule_type: 'weekly', schedule: { type: 'weekly', day: 'sunday', at: '07:50' }, priority: 3, location: 'Nationwide, IL' },
  // RUNPANEL — nationwide running race HTML calendar (runpanel.co.il); many rows link to Shvoong microsites
  { name: 'runpanel', enabled: true, schedule_type: 'weekly', schedule: { type: 'weekly', day: 'sunday', at: '07:55' }, priority: 3, location: 'Nationwide, IL' },
  // Go-Out — nationwide category feeds (nightlife, festivals, sports, weekend)
  { name: 'go_out', enabled: true, schedule_type: 'weekly', schedule: { type: 'weekly', day: 'sunday', at: '03:45' }, priority: 2, location: 'Nationwide, IL' },
  // Meetup — in-person events across selected Israel city searches
  { name: 'meetup', enabled: true, schedule_type: 'weekly', schedule: { type: 'weekly', day: 'sunday', at: '15:45' }, priority: 3, location: 'General, IL' },
  // LAStartup — in-person startup/tech event hub
  { name: 'lastartup', enabled: true, schedule_type: 'weekly', schedule: { type: 'weekly', day: 'sunday', at: '15:50' }, priority: 3, location: 'General, IL' },
  // Muzi — nationwide live-music aggregator (gap-fill; dedupes against ticket sources)
  { name: 'muzi', enabled: true, schedule_type: 'weekly', schedule: { type: 'weekly', day: 'sunday', at: '16:10' }, priority: 5, location: 'Nationwide, IL' },
  // Nationwide cinema (seret.co.il) — must run locally (fast dedupe + postTimes sync)
  { name: 'cinema', enabled: true, schedule_type: 'weekly', schedule: { type: 'weekly', day: 'thursday', at: '07:00' }, priority: 2, location: 'General, IL' },
  // Tel Aviv single-venue scrapers that currently only run locally
  { name: 'zappa', enabled: true, schedule_type: 'weekly', schedule: { type: 'weekly', day: 'sunday', at: '05:30' }, priority: 4, location: 'General, IL' },
  { name: 'tickchak_live', enabled: true, schedule_type: 'weekly', schedule: { type: 'weekly', day: 'sunday', at: '06:00' }, priority: 4, location: 'General, IL' },
  { name: 'cameri', enabled: true, schedule_type: 'weekly', schedule: { type: 'weekly', day: 'sunday', at: '05:15' }, priority: 4, location: 'Tel Aviv, IL' },
  { name: 'radical_house', enabled: true, schedule_type: 'weekly', schedule: { type: 'weekly', day: 'sunday', at: '09:00' }, priority: 5, location: 'Tel Aviv, IL' },
  { name: 'enav_tlv', enabled: true, schedule_type: 'weekly', schedule: { type: 'weekly', day: 'sunday', at: '09:12' }, priority: 5, location: 'Tel Aviv, IL' },
  { name: 'beit_hana', enabled: true, schedule_type: 'weekly', schedule: { type: 'weekly', day: 'sunday', at: '09:30' }, priority: 5, location: 'Tel Aviv, IL' },
  { name: 'bar_giyora', enabled: true, schedule_type: 'weekly', schedule: { type: 'weekly', day: 'sunday', at: '10:00' }, priority: 5, location: 'Tel Aviv, IL' },
  { name: 'bella_ciao', enabled: true, schedule_type: 'weekly', schedule: { type: 'weekly', day: 'sunday', at: '10:05' }, priority: 5, location: 'Tel Aviv, IL' },
  { name: 'havana_music_club', enabled: true, schedule_type: 'weekly', schedule: { type: 'weekly', day: 'sunday', at: '10:06' }, priority: 5, location: 'Tel Aviv, IL' },
  { name: 'gula', enabled: true, schedule_type: 'weekly', schedule: { type: 'weekly', day: 'sunday', at: '10:07' }, priority: 5, location: 'Petah Tikva, IL' },
  { name: 'ozen_tel_aviv', enabled: true, schedule_type: 'weekly', schedule: { type: 'weekly', day: 'sunday', at: '10:45' }, priority: 4, location: 'Tel Aviv, IL' },
  { name: 'lauter', enabled: true, schedule_type: 'weekly', schedule: { type: 'weekly', day: 'sunday', at: '10:48' }, priority: 4, location: 'Tel Aviv, IL' },
  // Matmon — מטמון, בית רומנו (matmon.space)
  { name: 'matmon', enabled: true, schedule_type: 'weekly', schedule: { type: 'weekly', day: 'sunday', at: '10:47' }, priority: 4, location: 'Tel Aviv, IL' },
  // Hameretz 2 — hameretz2.org (Florentin culture venue)
  { name: 'hameretz_2', enabled: true, schedule_type: 'weekly', schedule: { type: 'weekly', day: 'sunday', at: '10:50' }, priority: 4, location: 'Tel Aviv, IL' },
  // Mike's Place — live music per branch (Google Calendar iCal)
  { name: 'mikes_place_tlv', enabled: true, schedule_type: 'weekly', schedule: { type: 'weekly', day: 'sunday', at: '06:32' }, priority: 4, location: 'Tel Aviv, IL' },
  { name: 'mikes_place_jerusalem', enabled: true, schedule_type: 'weekly', schedule: { type: 'weekly', day: 'sunday', at: '06:33' }, priority: 4, location: 'Jerusalem, IL' },
  { name: 'mikes_place_eilat', enabled: true, schedule_type: 'weekly', schedule: { type: 'weekly', day: 'sunday', at: '06:34' }, priority: 4, location: 'Eilat, IL' },
  // Jerusalem Municipality city events (local-only)
  { name: 'jerusalem_muni', enabled: true, schedule_type: 'weekly', schedule: { type: 'weekly', day: 'sunday', at: '11:00' }, priority: 3, location: 'Jerusalem, IL' },
  // Nocturno – music and culture venue in Jerusalem
  { name: 'nocturno', enabled: true, schedule_type: 'weekly', schedule: { type: 'weekly', day: 'sunday', at: '11:30' }, priority: 4, location: 'Jerusalem, IL' },
  // Jerusalem Theater – multi-genre event hall in Jerusalem
  { name: 'jerusalem_theater', enabled: true, schedule_type: 'weekly', schedule: { type: 'weekly', day: 'sunday', at: '12:00' }, priority: 4, location: 'Jerusalem, IL' },
  // Khan Theater – quality theater venue in Jerusalem
  { name: 'khan_theater', enabled: true, schedule_type: 'weekly', schedule: { type: 'weekly', day: 'sunday', at: '12:30' }, priority: 4, location: 'Jerusalem, IL' },
  // Orna Porat Theater — children's theater (porat-theater.co.il), Tel Aviv / Gush Dan halls
  { name: 'porat_theater', enabled: true, schedule_type: 'weekly', schedule: { type: 'weekly', day: 'sunday', at: '12:32' }, priority: 4, location: 'Tel Aviv, IL' },
  // Habait Theatre — Jaffa (habait-theatre.org.il)
  { name: 'habait_theatre', enabled: true, schedule_type: 'weekly', schedule: { type: 'weekly', day: 'sunday', at: '12:38' }, priority: 4, location: 'Tel Aviv, IL' },
  { name: 'beit_avi_chai', enabled: true, schedule_type: 'weekly', schedule: { type: 'weekly', day: 'sunday', at: '12:45' }, priority: 4, location: 'Jerusalem, IL' },
  { name: 'hansen_house', enabled: true, schedule_type: 'weekly', schedule: { type: 'weekly', day: 'sunday', at: '12:55' }, priority: 4, location: 'Jerusalem, IL' },
  { name: 'first_station_jerusalem', enabled: true, schedule_type: 'weekly', schedule: { type: 'weekly', day: 'sunday', at: '13:20' }, priority: 4, location: 'Jerusalem, IL' },
  { name: 'jerusalem_cinematheque', enabled: true, schedule_type: 'weekly', schedule: { type: 'weekly', day: 'sunday', at: '13:40' }, priority: 4, location: 'Jerusalem, IL' },
  // Kivunim – Beer Sheva city culture/events portal
  { name: 'kivunim', enabled: true, schedule_type: 'weekly', schedule: { type: 'weekly', day: 'sunday', at: '13:00' }, priority: 3, location: 'Beer Sheva, IL' },
  // Ramat Gan municipal Smarticket (ramat-gan.smarticket.co.il)
  { name: 'ramat_gan_municipal_smarticket', enabled: true, schedule_type: 'weekly', schedule: { type: 'weekly', day: 'sunday', at: '13:08' }, priority: 3, location: 'Ramat Gan, IL' },
  { name: 'herzliya_municipal_smarticket', enabled: true, schedule_type: 'weekly', schedule: { type: 'weekly', day: 'sunday', at: '13:09' }, priority: 3, location: 'Herzliya, IL' },
  { name: 'hoh_herzliya_smarticket', enabled: true, schedule_type: 'weekly', schedule: { type: 'weekly', day: 'sunday', at: '13:10' }, priority: 3, location: 'Herzliya, IL' },
  // Beit Emmanuel Ramat Gan (mbe-rg.smarticket.co.il)
  { name: 'beit_emmanuel_rg', enabled: true, schedule_type: 'weekly', schedule: { type: 'weekly', day: 'sunday', at: '13:12' }, priority: 3, location: 'Ramat Gan, IL' },
  // Holon — תיאטרון חולון + מדיטק Smarticket + עירייה טפסים (forms.holon.muni.il)
  { name: 'holon_theater_smarticket', enabled: true, schedule_type: 'weekly', schedule: { type: 'weekly', day: 'sunday', at: '13:18' }, priority: 3, location: 'Holon, IL' },
  { name: 'holon_mediatheque_smarticket', enabled: true, schedule_type: 'weekly', schedule: { type: 'weekly', day: 'sunday', at: '13:19' }, priority: 3, location: 'Holon, IL' },
  { name: 'holon_municipal_forms', enabled: true, schedule_type: 'weekly', schedule: { type: 'weekly', day: 'sunday', at: '13:22' }, priority: 3, location: 'Holon, IL' },
  // Mishkan 7 – Beer Sheva performing arts center (mishkan7.co.il)
  { name: 'mishkan7', enabled: true, schedule_type: 'weekly', schedule: { type: 'weekly', day: 'sunday', at: '13:15' }, priority: 3, location: 'Beer Sheva, IL' },
  // Tel Aviv Youth Unit (linktr.ee → coing.co)
  { name: 'youngtlv', enabled: true, schedule_type: 'weekly', schedule: { type: 'weekly', day: 'sunday', at: '14:00' }, priority: 4, location: 'Tel Aviv, IL' },
  // Secret Tel Aviv — curated tickets (multi-city Israel)
  { name: 'secret_tel_aviv', enabled: true, schedule_type: 'weekly', schedule: { type: 'weekly', day: 'sunday', at: '14:01' }, priority: 4, location: 'General, IL' },
  // Geektime — tech/startup event board; physical cards only
  { name: 'geektime', enabled: true, schedule_type: 'weekly', schedule: { type: 'weekly', day: 'sunday', at: '14:03' }, priority: 4, location: 'General, IL' },
  // מזא״ה 9 — coing.co community calendar
  { name: 'mazeh9', enabled: true, schedule_type: 'weekly', schedule: { type: 'weekly', day: 'sunday', at: '14:02' }, priority: 4, location: 'Tel Aviv, IL' },
  { name: 'haezor', enabled: true, schedule_type: 'weekly', schedule: { type: 'weekly', day: 'sunday', at: '14:05' }, priority: 4, location: 'Tel Aviv, IL' },
  { name: 'beit_ariela', enabled: true, schedule_type: 'weekly', schedule: { type: 'weekly', day: 'sunday', at: '14:15' }, priority: 4, location: 'Tel Aviv, IL' },
  { name: 'bialik_museum', enabled: true, schedule_type: 'weekly', schedule: { type: 'weekly', day: 'sunday', at: '14:30' }, priority: 4, location: 'Tel Aviv, IL' },
  { name: 'tamuseum', enabled: true, schedule_type: 'weekly', schedule: { type: 'weekly', day: 'sunday', at: '04:00' }, priority: 4, location: 'Tel Aviv, IL' },
  { name: 'gutmanmuseum', enabled: true, schedule_type: 'weekly', schedule: { type: 'weekly', day: 'sunday', at: '14:35' }, priority: 4, location: 'Tel Aviv, IL' },
  { name: 'shablul_jazz', enabled: true, schedule_type: 'weekly', schedule: { type: 'weekly', day: 'sunday', at: '14:45' }, priority: 4, location: 'Tel Aviv, IL' },
  { name: 'hangar_11', enabled: true, schedule_type: 'weekly', schedule: { type: 'weekly', day: 'sunday', at: '15:00' }, priority: 4, location: 'Tel Aviv, IL' },
  { name: 'lieblinghaus', enabled: true, schedule_type: 'weekly', schedule: { type: 'weekly', day: 'sunday', at: '15:20' }, priority: 4, location: 'Tel Aviv, IL' },
  { name: 'done_drom_sharon', enabled: true, schedule_type: 'weekly', schedule: { type: 'weekly', day: 'sunday', at: '15:05' }, priority: 4, location: 'Hod HaSharon, IL' },
  // HechalDS — South Sharon cultural hall (smarticket)
  { name: 'hechalds', enabled: true, schedule_type: 'weekly', schedule: { type: 'weekly', day: 'sunday', at: '15:10' }, priority: 4, location: 'Hod HaSharon, IL' },
  { name: 'netanya_smarticket', enabled: true, schedule_type: 'weekly', schedule: { type: 'weekly', day: 'sunday', at: '15:25' }, priority: 4, location: 'Netanya, IL' },
  { name: 'eilat_muni_smarticket', enabled: true, schedule_type: 'weekly', schedule: { type: 'weekly', day: 'sunday', at: '15:26' }, priority: 4, location: 'Eilat, IL' },
  { name: 'eilat_matnasim_smarticket', enabled: true, schedule_type: 'weekly', schedule: { type: 'weekly', day: 'sunday', at: '15:27' }, priority: 4, location: 'Eilat, IL' },
  { name: 'sifrianet_netanya', enabled: true, schedule_type: 'weekly', schedule: { type: 'weekly', day: 'sunday', at: '15:40' }, priority: 4, location: 'Netanya, IL' },
  // Jaffa Cinema — Tel Aviv venue
  { name: 'jaffacinema', enabled: true, schedule_type: 'weekly', schedule: { type: 'weekly', day: 'thursday', at: '07:10' }, priority: 4, location: 'Tel Aviv, IL' },
  // Gray Club — live clubs in Modiin, Yehud, Tel Aviv (grayclub.co.il)
  { name: 'grayclub', enabled: true, schedule_type: 'weekly', schedule: { type: 'weekly', day: 'sunday', at: '15:18' }, priority: 4, location: 'General, IL' },
  // Aguda LGBT — nationwide Pride 2026 calendar (lgbt.org.il/events2026)
  { name: 'aguda_lgbt', enabled: true, schedule_type: 'weekly', schedule: { type: 'weekly', day: 'sunday', at: '15:12' }, priority: 4, location: 'General, IL' },
  // Israel Innovation Authority — in-person innovation events only (skips webinars / Zoom)
  { name: 'israel_innovation', enabled: true, schedule_type: 'weekly', schedule: { type: 'weekly', day: 'sunday', at: '15:14' }, priority: 4, location: 'General, IL' },
  // Ashdod batch 1
  { name: 'ironit_ashdod', enabled: true, schedule_type: 'weekly', schedule: { type: 'weekly', day: 'sunday', at: '15:25' }, priority: 3, location: 'Ashdod, IL' },
  { name: 'mishkan_ashdod', enabled: true, schedule_type: 'weekly', schedule: { type: 'weekly', day: 'sunday', at: '15:30' }, priority: 4, location: 'Ashdod, IL' },
  { name: 'ashdod_art_museum', enabled: true, schedule_type: 'weekly', schedule: { type: 'weekly', day: 'sunday', at: '15:35' }, priority: 4, location: 'Ashdod, IL' },
  { name: 'ashdodnet', enabled: true, schedule_type: 'weekly', schedule: { type: 'weekly', day: 'sunday', at: '15:40' }, priority: 4, location: 'Ashdod, IL' },
];

/** Every seeded local-only row must use the local/Firestore queue path, not Cloud Run `/scrape/{name}`. */
const LOCAL_ONLY_SCRAPER_NAMES_SET = new Set(LOCAL_ONLY_SCRAPERS.map((s) => s.name));

/** Fallback if /api/orchestration/local-runnable fails (must match adapter_factory registrations). */
const LOCAL_RUN_SCRAPER_NAMES = [
  'tel_aviv_gov', 'mevalim', 'facebook_logout', 'chillz', 'mugics', 'goshow_standup', 'amama',
  'zappa', 'tickchak_live', 'cameri', 'radical_house', 'enav_tlv', 'beit_hana', 'bar_giyora', 'bella_ciao', 'havana_music_club', 'gula', 'ozen_tel_aviv', 'lauter', 'matmon', 'hameretz_2', 'mikes_place_tlv', 'mikes_place_jerusalem', 'mikes_place_eilat', 'haifamuni', 'haifa_museums', 'beit_hagefen', 'madatech_tickets', 'ethos_haifa', 'aluma_hod', 'siloculture', 'greenbear', 'ibasketball', 'shvoong', 'runpanel',
  'jerusalem_muni', 'comy', 'go_out', 'cinema', 'nocturno', 'jerusalem_theater', 'khan_theater', 'porat_theater', 'habait_theatre',
  'beit_avi_chai', 'hansen_house', 'first_station_jerusalem', 'jerusalem_cinematheque',
  'kivunim', 'ramat_gan_municipal_smarticket', 'herzliya_municipal_smarticket', 'hoh_herzliya_smarticket', 'beit_emmanuel_rg',
  'holon_theater_smarticket', 'holon_mediatheque_smarticket', 'holon_municipal_forms',
  'mishkan7', 'youngtlv', 'secret_tel_aviv', 'geektime', 'mazeh9', 'haezor', 'beit_ariela', 'bialik_museum', 'tamuseum', 'gutmanmuseum', 'shablul_jazz', 'hangar_11', 'lieblinghaus',   'done_drom_sharon', 'jaffacinema',
  'hechalds',
  'netanya_smarticket', 'eilat_muni_smarticket', 'eilat_matnasim_smarticket', 'sifrianet_netanya',
  'meetup', 'lastartup', 'muzi',
  'grayclub', 'aguda_lgbt', 'israel_innovation',
  'ironit_ashdod', 'mishkan_ashdod', 'ashdod_art_museum', 'ashdodnet',
  'tmuna', 'bravo',
  // Multi-city variants
  'facebook_logout_haifa', 'facebook_logout_jerusalem',
  'facebook_logout_beersheva', 'facebook_logout_ashdod', 'facebook_logout_rishonlezion',
];

/** These scrapers must always display and sort as priority 5 (API may return -5). */
const PRIORITY_5_DISPLAY_NAMES = ['radical_house', 'enav_tlv', 'beit_hana', 'bar_giyora', 'bella_ciao', 'havana_music_club', 'gula'];

/** Nationwide (all-Israel) scrapers – display as "General, IL" in table and filter. */
const NATIONWIDE_SCRAPERS = ['mevalim', 'mugics', 'goshow_standup', 'comy', 'go_out', 'meetup', 'lastartup', 'muzi', 'cinema', 'zappa', 'tickchak_live', 'ibasketball', 'grayclub', 'aguda_lgbt', 'israel_innovation', 'shvoong', 'runpanel', 'secret_tel_aviv', 'geektime'];

/** Same as backend: Nationwide and General are one bucket (all of Israel). */
function normalizeLocationLabel(loc: string | undefined): string {
  if (!loc || typeof loc !== 'string') return '';
  const t = loc.trim();
  if (/^nationwide\s*,\s*il$/i.test(t)) return 'General, IL';
  return t;
}

function getDisplayLocation(scraper: ScraperStatus): string {
  if (NATIONWIDE_SCRAPERS.includes(scraper.name)) return 'General, IL';
  const loc = normalizeLocationLabel(scraper.location);
  if (loc === 'Israel') return 'General, IL';
  return loc;
}

/** Display name for table: originals show as _telaviv so city variants are clear. Backend key unchanged. */
const SCRAPER_DISPLAY_NAMES: Record<string, string> = {
  facebook_logout: 'facebook_logout_telaviv',
  jerusalem_muni: 'Jerusalem Municipality',
  haifamuni: 'Haifa Municipality',
  haifa_museums: 'Haifa Museums (HMA + HMS)',
  beit_hagefen: 'Beit Hagefen',
  madatech_tickets: 'Madatech (tickets)',
  ethos_haifa: 'Ethos Haifa (ethos.co.il)',
  aluma_hod: 'Aluma Hod (Hod Hasharon)',
  siloculture: 'Silo Culture (Hod Hasharon)',
  greenbear: 'Greenbear (הדוב הירוק)',
  ibasketball: 'iBasketball (IBBA)',
  shvoong: 'Shvoong (שוונג — לוח ספורט)',
  runpanel: 'RUNPANEL (לוח ריצה)',
  go_out: 'Go-Out (category feeds)',
  meetup: 'Meetup (in-person Israel)',
  lastartup: 'LAStartup (in-person tech)',
  muzi: 'Muzi (live-music aggregator)',
  cinema: 'Cinema (seret.co.il)',
  khan_theater: 'Khan Theater',
  porat_theater: 'Porat Theater (אורנה פורת)',
  habait_theatre: 'Habait Theatre (תיאטרון הבית)',
  nocturno: 'Nocturno',
  jerusalem_theater: 'Jerusalem Theater',
  beit_avi_chai: 'Beit Avi Chai',
  hansen_house: 'Hansen House',
  first_station_jerusalem: 'First Station Jerusalem',
  jerusalem_cinematheque: 'Jerusalem Cinematheque',
  kivunim: 'Kivunim Beer Sheva',
  ramat_gan_municipal_smarticket: 'Ramat Gan (municipal Smarticket)',
  herzliya_municipal_smarticket: 'Herzliya (municipal תנו״ס Smarticket)',
  hoh_herzliya_smarticket: 'HOH Herzliya (היכל אמנויות הבמה)',
  beit_emmanuel_rg: 'Beit Emmanuel Ramat Gan',
  holon_theater_smarticket: 'Holon Theater (תיאטרון חולון Smarticket)',
  holon_mediatheque_smarticket: 'Holon Mediatech (מדיטק Smarticket)',
  holon_municipal_forms: 'Holon Municipality (forms / אירועים)',
  mishkan7: 'Mishkan 7 (Beer Sheva)',
  youngtlv: 'Young TLV (יחידת הצעירים)',
  secret_tel_aviv: 'Secret Tel Aviv (tickets)',
  geektime: 'Geektime (physical events)',
  mazeh9: 'מזאה 9 (צעירים עושים עיר)',
  haezor: 'Haezor (מועדון האזור)',
  beit_ariela: 'Beit Ariela',
  ozen_tel_aviv: 'Ozen Tel Aviv',
  lauter: 'Lauter (לאוטר)',
  matmon: 'Matmon (מטמון)',
  hameretz_2: 'Hameretz 2 (המרץ2)',
  enav_tlv: 'Enav TLV (מרכז ענב)',
  mikes_place_tlv: "Mike's Place Tel Aviv (live music)",
  mikes_place_jerusalem: "Mike's Place Jerusalem (live music)",
  mikes_place_eilat: "Mike's Place Eilat (live music)",
  bialik_museum: 'Bialik Museum',
  tamuseum: 'Tel Aviv Museum of Art',
  gutmanmuseum: 'Gutman Museum (נחום גוטמן)',
  shablul_jazz: 'Shablul Jazz',
  hangar_11: 'Hangar 11',
  lieblinghaus: 'Liebling Haus',
  done_drom_sharon: 'D-ONE Drom Sharon',
  hechalds: 'HechalDS (היכל התרבות דרום השרון)',
  netanya_smarticket: 'Netanya Smarticket (היכל התרבות נתניה)',
  eilat_muni_smarticket: 'Eilat municipal Smarticket (עיריית אילת)',
  eilat_matnasim_smarticket: 'Eilat Matnas Smarticket (רשת המתנ״סים)',
  sifrianet_netanya: 'Sifrianet Netanya (ספריות נתניה)',
  tickchak_live: 'Tickchak LIVE',
  bella_ciao: "Bella Ciao (Tickchak LIVE — בלה צ'או בר)",
  havana_music_club: 'Havana Music Club (הוואנה קלאב)',
  gula: 'Gula (גולה מתחם תרבות ופנאי — Tickchak)',
  jaffacinema: 'Jaffa Cinema',
  grayclub: 'Gray Club (מועדון גריי)',
  aguda_lgbt: 'Aguda LGBT Pride (אגודה — אירועי גאווה 2026)',
  israel_innovation: 'Israel Innovation Authority',
  mugics: 'Mugics (live music)',
  tel_aviv_gov: 'Tel Aviv Municipality (gov.il)',
  mevalim: 'Mevalim',
  chillz: 'Chillz',
  amama: 'AMAMA (GoShow — חצרים 15)',
  ironit_ashdod: 'Ironit Ashdod',
  mishkan_ashdod: 'Mishkan Ashdod',
  ashdod_art_museum: 'Ashdod Art Museum',
  ashdodnet: 'AshdodNet',
  // goshow_standup, mugics, mevalim are nationwide – no _telaviv suffix
};
function getScraperDisplayName(name: string): string {
  return SCRAPER_DISPLAY_NAMES[name] ?? name;
}

function displayPriority(scraper: ScraperStatus): number {
  return PRIORITY_5_DISPLAY_NAMES.includes(scraper.name) ? 5 : (scraper.priority ?? 999);
}

type ScheduleSortColumn = 'name' | 'location' | 'schedule' | 'lastRun' | 'priority' | 'health' | 'trigger';

function getLastSuccessfulRunMs(
  scraper: ScraperStatus,
  latestReports: Record<string, ScraperReport>
): number | null {
  const fromScraper = scraper.last_successful_run;
  const rep = latestReports[scraper.name];
  const fromReport =
    rep && rep.status === 'completed' ? (rep.end_time || rep.start_time) : undefined;
  const iso = fromScraper || fromReport;
  if (!iso) return null;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? null : t;
}

function healthRank(status: string): number {
  const order = ['healthy', 'degraded', 'unknown', 'unhealthy'];
  const i = order.indexOf(status);
  return i >= 0 ? i : 99;
}

/**
 * Merge Cloud orchestration row into seeded LOCAL_ONLY row.
 * API sometimes sends null/empty location or weak schedule objects; that would hide rows when
 * the user filters by location (e.g. Beer Sheva) or show "Unknown schedule".
 */
function mergeOrchestrationRow(
  local: ScraperStatus | undefined,
  api: ScraperStatus
): ScraperStatus {
  const merged: ScraperStatus = { ...(local || {}), ...api };
  if (local) {
    const apiLoc = api.location;
    if (apiLoc == null || (typeof apiLoc === 'string' && apiLoc.trim() === '')) {
      merged.location = local.location;
    }
    const st = merged.schedule;
    const hasValidSchedule =
      st &&
      typeof st === 'object' &&
      (st as { type?: string }).type &&
      (st as { type?: string }).type !== 'unknown';
    if (!hasValidSchedule && local.schedule && (local.schedule as { type?: string }).type) {
      merged.schedule = local.schedule;
    }
    // Hosted Cloud orchestration may still label adapter-backed scrapers as cloud_run until redeployed.
    if (api.trigger_mode === 'cloud_run') {
      merged.trigger_mode = local.trigger_mode ?? 'scheduled_local';
    }
  }
  return merged;
}

export const ScraperOrchestration: React.FC = () => {
  const [status, setStatus] = useState<any>(null);
  const [health, setHealth] = useState<HealthStatus | null>(null);
  const [progress, setProgress] = useState<ProgressResponse>({ count: 0, progress: {}, timestamp: new Date().toISOString() });
  const [progressLoading, setProgressLoading] = useState(true);
  const [progressError, setProgressError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState<Set<string>>(new Set());
  const [cancelling, setCancelling] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  /** When user triggers a local run from deployed site, we keep it for scroll/UX. */
  const [activeLocalRunId, setActiveLocalRunId] = useState<string | null>(null);
  const [activeLocalRunAt, setActiveLocalRunAt] = useState<number | null>(null);
  /** Run IDs that came from local server – used so Cancel hits local even after refresh/navigate. */
  const [localRunIds, setLocalRunIds] = useState<Set<string>>(new Set());
  /** Latest completed report per scraper – used to backfill "Last successful run" when orchestration API omits it. */
  const [latestReports, setLatestReports] = useState<Record<string, ScraperReport>>({});
  /** Location filter for the scrapers schedule table. Empty = show all; non-empty = show scrapers in selected locations. */
  const [locationFilter, setLocationFilter] = useState<string[]>([]);
  /** Substring match on scraper key or display name (table header filter). */
  const [nameFilter, setNameFilter] = useState('');
  const [scheduleSort, setScheduleSort] = useState<{ column: ScheduleSortColumn | null; dir: 'asc' | 'desc' }>({
    column: null,
    dir: 'asc',
  });
  const [headerLocationText, setHeaderLocationText] = useState('');
  const [headerScheduleText, setHeaderScheduleText] = useState('');
  const [headerPriority, setHeaderPriority] = useState('');
  const [headerHealth, setHeaderHealth] = useState('');
  const [headerTrigger, setHeaderTrigger] = useState('');
  /** Local server status from Firestore heartbeat */
  const [localServerStatus, setLocalServerStatus] = useState<LocalServerStatus | null>(null);
  const [localServerBusy, setLocalServerBusy] = useState(false);
  const [localServerMsg, setLocalServerMsg] = useState<{ text: string; type: 'success' | 'error' } | null>(null);
  /** Scrapers with a registered local adapter (from API; keeps dashboard in sync with adapter_factory). */
  const [localRunnableNames, setLocalRunnableNames] = useState<Set<string>>(
    () => new Set(LOCAL_RUN_SCRAPER_NAMES)
  );
  /** Firestore `local_automation_batches/{id}` — full automatic multi-scraper run */
  const [fullAutoBatchId, setFullAutoBatchId] = useState<string | null>(null);
  const [fullAutoBatch, setFullAutoBatch] = useState<LocalAutomationBatch | null>(null);
  const [pendingLocalQueue, setPendingLocalQueue] = useState<LocalPendingQueueItem[]>([]);
  const [doctorOverview, setDoctorOverview] = useState<ScraperDoctorOverview | null>(null);
  const [doctorReview, setDoctorReview] = useState<ScraperDoctorReview | null>(null);
  const [doctorReviewOpen, setDoctorReviewOpen] = useState(false);
  const [doctorBusy, setDoctorBusy] = useState<string | null>(null);
  const [doctorRefreshing, setDoctorRefreshing] = useState(false);

  useEffect(() => {
    loadData();
    // Refresh every 30 seconds
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    fetchLatestRunningAutomationBatch().then((id) => {
      if (id) setFullAutoBatchId((prev) => prev ?? id);
    });
  }, []);

  useEffect(() => {
    if (!fullAutoBatchId) {
      setFullAutoBatch(null);
      return () => {};
    }
    return subscribeLocalAutomationBatch(fullAutoBatchId, setFullAutoBatch);
  }, [fullAutoBatchId]);

  // Poll local server status via Firestore heartbeat
  useEffect(() => {
    const check = async () => {
      const s = await checkLocalServerRunning();
      setLocalServerStatus(s);
    };
    check();
    const interval = setInterval(check, 8000);
    return () => clearInterval(interval);
  }, []);

  // Resolve localhost port (8101–8103) as soon as heartbeat says the server is up, so Live Progress does not hit a stale empty API base.
  useEffect(() => {
    if (localServerStatus?.alive) {
      void getLocalApiBase();
    }
  }, [localServerStatus?.alive]);

  useEffect(() => {
    if (!localServerStatus?.alive) return;
    getScraperDoctorOverview({ forceLocal: true })
      .then((data) => setDoctorOverview(data))
      .catch(() => {});
  }, [localServerStatus?.alive, localServerStatus?.serverPort]);

  // Real-time progress from Firestore heartbeat (~1.5s sync from local server).
  useEffect(() => {
    return subscribeLocalServerProgress((firestoreProgress) => {
      if (Object.keys(firestoreProgress).length === 0) return;
      setProgress((prev) => {
        const merged: Record<string, ProgressInfo> = { ...prev.progress };
        for (const [runId, row] of Object.entries(firestoreProgress)) {
          const existing = merged[runId];
          if (!existing) {
            merged[runId] = row;
            continue;
          }
          const existingTs = new Date(existing.updated_at || 0).getTime();
          const rowTs = new Date(row.updated_at || 0).getTime();
          merged[runId] = rowTs >= existingTs ? { ...existing, ...row } : existing;
        }
        return {
          ...prev,
          count: Object.keys(merged).length,
          progress: merged,
        };
      });
      setProgressLoading(false);
    });
  }, []);

  // Poll progress every 2s: fetch BOTH local and cloud, merge so progress loads automatically and persists when navigating back.
  const LOCAL_RUN_POLL_MS = 30 * 60 * 1000;
  useEffect(() => {
    const pollProgress = async () => {
      try {
        setProgressError(null);
        const now = Date.now();
        if ((localServerStatus?.runningCount ?? 0) > 0) {
          await getLocalApiBase(true);
        }
        const [localData, cloudData, firestoreProgressData] = await Promise.all([
          getLocalProgress(),
          getProgress().catch(() => ({ count: 0, progress: {} as Record<string, ProgressInfo>, timestamp: new Date().toISOString() })),
          fetchLocalProgressFromFirestore(),
        ]);

        const localProgress = localData && 'progress' in localData ? (localData as ProgressResponse).progress : {};
        const cloudProgress = cloudData && 'progress' in cloudData ? (cloudData as ProgressResponse).progress : {};
        const mergedProgress: Record<string, ProgressInfo> = {
          ...cloudProgress,
          ...firestoreProgressData,
          ...localProgress,
        };
        const count = Object.keys(mergedProgress).length;

        if (Object.keys(localProgress).length > 0) {
          setLocalRunIds(prev => {
            const next = new Set(prev);
            Object.keys(localProgress).forEach(id => next.add(id));
            return next;
          });
        }

        setProgress({
          count,
          progress: mergedProgress,
          timestamp: (cloudData as ProgressResponse)?.timestamp ?? new Date().toISOString(),
        });
        setProgressLoading(false);

        if (localServerStatus?.alive || localServerStatus?.watcherAlive) {
          const pending = await fetchLocalPendingQueue();
          setPendingLocalQueue(pending);
        } else {
          setPendingLocalQueue([]);
        }

        if (activeLocalRunId && activeLocalRunAt && (now - activeLocalRunAt < LOCAL_RUN_POLL_MS)) {
          const runInfo = mergedProgress[activeLocalRunId];
          if (runInfo && ['completed', 'error', 'cancelled'].includes(runInfo.status)) {
            setTimeout(() => {
              setActiveLocalRunId(null);
              setActiveLocalRunAt(null);
            }, 2 * 60 * 1000);
          }
        }
      } catch (error: any) {
        console.error('Error polling progress:', error);
        setProgressError(error?.message || 'Failed to load progress data');
        setProgressLoading(false);
        setProgress({ count: 0, progress: {}, timestamp: new Date().toISOString() });
      }
    };

    pollProgress();
    const progressInterval = setInterval(pollProgress, 2000);
    return () => clearInterval(progressInterval);
  }, [
    activeLocalRunId,
    activeLocalRunAt,
    localServerStatus?.alive,
    localServerStatus?.watcherAlive,
    localServerStatus?.runningCount,
  ]);

  const handleStartServer = async () => {
    if (localServerStatus?.alive) {
      const port = localServerStatus.serverPort ? ` (port ${localServerStatus.serverPort})` : '';
      setLocalServerMsg({ text: `Server is already running${port}.`, type: 'success' });
      return;
    }
    if (localServerBusy) return;
    setLocalServerBusy(true);
    setLocalServerMsg(null);
    try {
      const result = await startLocalServer();
      setLocalServerMsg({ text: result.message, type: result.ok ? 'success' : 'error' });
      if (result.ok && !result.skipped) {
        setTimeout(async () => {
          setLocalServerStatus(await checkLocalServerRunning());
        }, 5000);
      } else if (result.ok) {
        setLocalServerStatus(await checkLocalServerRunning());
      }
    } catch (e: any) {
      setLocalServerMsg({ text: e?.message || 'Failed to start', type: 'error' });
    } finally {
      setLocalServerBusy(false);
    }
  };

  const handleServerCommand = async (cmd: 'stop' | 'kill_and_restart') => {
    setLocalServerBusy(true);
    setLocalServerMsg(null);
    try {
      const { result } = await sendLocalServerCommand(cmd);
      setLocalServerMsg({ text: result, type: 'success' });
      setFullAutoBatchId(null);
      setFullAutoBatch(null);
      // Re-check after a moment
      setTimeout(async () => {
        setLocalServerStatus(await checkLocalServerRunning());
      }, 3000);
    } catch (e: any) {
      setLocalServerMsg({ text: e?.message || 'Command failed', type: 'error' });
    } finally {
      setLocalServerBusy(false);
    }
  };

  const handleStopScraping = async () => {
    setLocalServerBusy(true);
    setLocalServerMsg(null);
    try {
      const { result } = await sendLocalServerCommand('stop_scraping');
      setLocalServerMsg({ text: result, type: 'success' });
      setFullAutoBatchId(null);
      setFullAutoBatch(null);
      setTimeout(async () => {
        setLocalServerStatus(await checkLocalServerRunning());
      }, 2000);
    } catch (e: any) {
      setLocalServerMsg({ text: e?.message || 'Stop scraping failed', type: 'error' });
    } finally {
      setLocalServerBusy(false);
    }
  };

  const handleFullAutomaticLocalRun = async () => {
    if (localServerBusy) return;
    setLocalServerBusy(true);
    setLocalServerMsg(null);
    try {
      const { result, batch_id } = await sendLocalServerCommand('run_full_automatic');
      setLocalServerMsg({ text: result, type: 'success' });
      if (batch_id) setFullAutoBatchId(batch_id);
      setTimeout(() => {
        loadData();
        void checkLocalServerRunning().then(setLocalServerStatus);
      }, 1500);
    } catch (e: any) {
      setLocalServerMsg({ text: e?.message || 'Automatic local run failed', type: 'error' });
      setLocalServerStatus(await checkLocalServerRunning());
    } finally {
      setLocalServerBusy(false);
    }
  };

  const loadData = async () => {
    try {
      setError(null);
      const [statusData, healthData, latestReportsData, localRunnableList, doctorData] = await Promise.all([
        getOrchestrationStatus(),
        getHealthStatus(),
        getLatestReports().catch(() => null),
        fetchLocalRunnableScrapers(),
        getScraperDoctorOverview({ forceLocal: true }).catch(() => null),
      ]);
      // Keep the dashboard's baked-in local list and merge API results on top.
      // This prevents hosted dashboard deploys from downgrading new local-only scrapers
      // to "Cloud Run" just because the remote cloud service hasn't been updated.
      setLocalRunnableNames(new Set([...LOCAL_RUN_SCRAPER_NAMES, ...localRunnableList]));
      setStatus(statusData);
      setHealth(healthData);
      if (doctorData) setDoctorOverview(doctorData);
      if (latestReportsData && (latestReportsData as any).latest_reports) {
        setLatestReports((latestReportsData as any).latest_reports as Record<string, ScraperReport>);
      } else {
        setLatestReports({});
      }
    } catch (error: any) {
      console.error('Error loading orchestration data:', error);
      const errorMessage = error?.message || String(error);
      setError(`Failed to load data: ${errorMessage}. Check browser console for details.`);
      // Set empty state on error
      setStatus({ status: 'error', total_scrapers: 0, scrapers: [], timezone: 'UTC' });
      setHealth({ overall: 'error', scrapers: {}, timestamp: new Date().toISOString() });
    } finally {
      setLoading(false);
    }
  };

  const refreshDoctor = async () => {
    if (doctorRefreshing) return;
    setDoctorRefreshing(true);
    try {
      const doctorData = await getScraperDoctorOverview({ forceLocal: true });
      setDoctorOverview(doctorData);
    } catch (error: any) {
      console.error('Error refreshing Scraper Doctor:', error);
      setLocalServerMsg({
        text: error?.message || 'Scraper Doctor refresh failed',
        type: 'error',
      });
    } finally {
      setDoctorRefreshing(false);
    }
  };

  const handleRunScraper = async (
    scraperName: string,
    triggerType: 'cloud' | 'manual_local' = 'cloud',
  ) => {
    if (triggering.has(scraperName)) return;

    setTriggering(prev => new Set(prev).add(scraperName));
    try {
      const result = await triggerScraper(scraperName, triggerType);
      if (result.status === 'queued') {
        const pending = await fetchLocalPendingQueue();
        setPendingLocalQueue(pending);
        alert(
          result.message ||
            `${scraperName} is queued. It will start automatically when the current local scrape finishes. ` +
              'See "Queued (waiting)" under Live Progress.',
        );
      } else {
        if (result.run_id && triggerType === 'manual_local') {
          setActiveLocalRunId(result.run_id);
          setActiveLocalRunAt(Date.now());
          setLocalRunIds((prev) => new Set(prev).add(result.run_id!));
        }
        if (triggerType === 'manual_local') {
          alert(
            result.message ||
              `${scraperName} started locally${result.run_id ? ` (Run ID: ${result.run_id})` : ''}.`,
          );
        }
      }
      // Immediately refresh progress to show the new run (merge local + cloud like the poller — never wipe on failed local fetch)
      const refreshProgress = async () => {
        try {
          if (triggerType === 'manual_local') {
            const [localData, cloudData] = await Promise.all([
              getLocalProgress(),
              getProgress().catch(() => ({
                count: 0,
                progress: {} as Record<string, ProgressInfo>,
                timestamp: new Date().toISOString(),
              })),
            ]);
            const localProgress =
              localData && 'progress' in localData ? (localData as ProgressResponse).progress : {};
            const cloudProgress =
              cloudData && 'progress' in cloudData ? (cloudData as ProgressResponse).progress : {};
            const mergedProgress: Record<string, ProgressInfo> = { ...cloudProgress, ...localProgress };
            setProgress({
              count: Object.keys(mergedProgress).length,
              progress: mergedProgress,
              timestamp: (cloudData as ProgressResponse)?.timestamp ?? new Date().toISOString(),
            });
            setProgressLoading(false);
            setProgressError(null);
          } else {
            const progressData = await getProgress();
            console.log('Progress data received:', progressData);
            if (progressData && 'progress' in progressData) {
              console.log('Setting progress with count:', progressData.count);
              setProgress(progressData);
              setProgressLoading(false);
              setProgressError(null);
            } else {
              console.warn('Progress data missing expected structure:', progressData);
              setProgressLoading(false);
            }
          }
        } catch (error: any) {
          console.error('Error refreshing progress:', error);
          setProgressError(error?.message || 'Failed to load progress');
          setProgressLoading(false);
        }
      };
      
      // Refresh immediately, then retry multiple times to catch progress initialization
      refreshProgress();
      setTimeout(() => {
        console.log('Retrying progress fetch (1s)...');
        refreshProgress();
      }, 1000);
      setTimeout(() => {
        console.log('Retrying progress fetch (3s)...');
        refreshProgress();
      }, 3000);
      setTimeout(() => {
        console.log('Retrying progress fetch (5s)...');
        refreshProgress();
      }, 5000);
      setTimeout(() => {
        console.log('Retrying progress fetch (10s)...');
        refreshProgress();
      }, 10000);
      
      // Reload orchestration data after a short delay
      setTimeout(() => {
        loadData();
      }, 2000);
    } catch (error: any) {
      alert(`Failed to trigger scraper: ${error.message || error}`);
    } finally {
      setTriggering(prev => {
        const newSet = new Set(prev);
        newSet.delete(scraperName);
        return newSet;
      });
    }
  };

  const handleDoctorReview = async (scraperName: string, queueTest = false) => {
    if (doctorBusy) return;
    setDoctorBusy(scraperName);
    try {
      const review = await getScraperDoctorReview(scraperName, { queueTest });
      setDoctorReview(review);
      setDoctorReviewOpen(true);
      if (queueTest && review.test_queue) {
        const pending = await fetchLocalPendingQueue();
        setPendingLocalQueue(pending);
        alert(review.test_queue.message || `${scraperName} test scrape queued for Scraper Doctor review.`);
      }
      if (queueTest) {
        setTimeout(() => {
          loadData();
        }, 1500);
      }
    } catch (err: any) {
      alert(`Scraper Doctor failed: ${err.message || err}`);
    } finally {
      setDoctorBusy(null);
    }
  };

  const handleCancelScraper = async (runId: string, scraperName: string) => {
    if (cancelling.has(runId)) return;
    
    if (!window.confirm(`Are you sure you want to cancel the running scraper: ${scraperName}?`)) {
      return;
    }
    
    setCancelling(prev => new Set(prev).add(runId));
    try {
      // Use local cancel endpoint when this run came from local server (works after refresh/navigate)
      const useLocal = localRunIds.has(runId);
      await cancelScraper(runId, useLocal);
      alert(`Cancel signal sent for ${scraperName} (Run ID: ${runId}). It will stop at the next event.`);
      const progressData = useLocal ? await getLocalProgress() : await getProgress();
      if (progressData && 'progress' in progressData) {
        setProgress(progressData);
      }
    } catch (error: any) {
      alert(`Failed to cancel scraper: ${error.message || error}`);
    } finally {
      setCancelling(prev => {
        const newSet = new Set(prev);
        newSet.delete(runId);
        return newSet;
      });
    }
  };

  const formatDuration = (seconds?: number) => {
    if (!seconds) return 'N/A';
    if (seconds < 60) return `${Math.round(seconds)}s`;
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    return `${mins}m ${secs}s`;
  };

  const formatSchedule = (scraper: ScraperStatus) => {
    const { schedule } = scraper;
    if (!schedule || typeof schedule !== 'object') {
      return scraper.schedule_deadline || 'Unknown schedule';
    }
    const st = schedule.type;
    if (st === 'weekly') {
      return `Every ${schedule.day} at ${schedule.at}`;
    }
    if (st === 'twice_weekly') {
      return `${schedule.days?.join(' & ') ?? '—'} at ${schedule.at}`;
    }
    if (st === 'daily') {
      return `Daily at ${schedule.at}`;
    }
    if (st === 'interval_days') {
      return `Every ${schedule.interval} days at ${schedule.at}`;
    }
    return scraper.schedule_deadline || 'Unknown schedule';
  };

  const getTriggerMode = (scraper: ScraperStatus, supportsLocal: boolean) => {
    // Hosted Cloud orchestration may still label adapter-backed scrapers as cloud_run until redeployed.
    if (supportsLocal && scraper.trigger_mode === 'cloud_run') {
      return 'scheduled_local';
    }
    if (scraper.trigger_mode) return scraper.trigger_mode;
    return supportsLocal ? 'scheduled_local' : 'cloud_run';
  };

  const getTriggerLabel = (triggerMode: string) => {
    if (triggerMode === 'scheduled_local') return 'Scheduled local';
    if (triggerMode === 'manual_local') return 'Manual local';
    return 'Cloud Run';
  };

  const getTriggerClassName = (triggerMode: string) => {
    if (triggerMode === 'scheduled_local') return 'scheduled-local';
    if (triggerMode === 'manual_local') return 'manual-local';
    return 'cloud';
  };

  const terminalBatchJobStatus = (st: string | undefined) => {
    const s = (st || 'pending').toLowerCase();
    return ['success', 'error', 'cancelled', 'completed', 'skipped'].includes(s);
  };

  const [fullAutoTableExpanded, setFullAutoTableExpanded] = useState(false);
  const [pendingQueueExpanded, setPendingQueueExpanded] = useState(false);
  const [completedRunsExpanded, setCompletedRunsExpanded] = useState(false);

  const batchPendingFromOrder = useMemo(() => {
    if (!fullAutoBatch?.order?.length) return [];
    const ss = fullAutoBatch.scraper_status || {};
    return fullAutoBatch.order.filter(
      (name) => String(ss[name] || 'pending').toLowerCase() === 'pending',
    );
  }, [fullAutoBatch]);

  /** Single queue source for auto + manual local runs (Firestore `local_run_queue`). */
  const displayQueueItems = useMemo((): LocalPendingQueueItem[] => {
    if (fullAutoBatchId && pendingLocalQueue.length > 0) {
      const forBatch = pendingLocalQueue.filter(
        (item) => !item.batch_id || item.batch_id === fullAutoBatchId,
      );
      if (forBatch.length > 0) return forBatch;
    }
    if (fullAutoBatchId && batchPendingFromOrder.length > 0) {
      return batchPendingFromOrder.map((name) => ({
        id: `batch-order-${name}`,
        scraper: name,
        requested_at: fullAutoBatch?.started_at,
        trigger_type: 'scheduled_local',
        batch_id: fullAutoBatchId,
      }));
    }
    return pendingLocalQueue;
  }, [pendingLocalQueue, fullAutoBatchId, batchPendingFromOrder, fullAutoBatch?.started_at]);

  const effectiveLaneLimits = useMemo(() => {
    const batchLimits = fullAutoBatch?.lane_limits;
    if (batchLimits && Object.keys(batchLimits).length > 0) return batchLimits;
    const doctorLimits =
      doctorOverview?.concurrency?.lane_limits ||
      doctorOverview?.lane_limits ||
      doctorOverview?.lanes;
    if (doctorLimits && Object.keys(doctorLimits).length > 0) return doctorLimits;
    return null;
  }, [fullAutoBatch?.lane_limits, doctorOverview]);

  const formatLaneLimits = (limits: Record<string, number> | null) => {
    if (!limits) return '—';
    return Object.entries(limits)
      .map(([lane, n]) => `${lane}:${n}`)
      .join(' · ');
  };

  const fullAutoProgressStats = useMemo(() => {
    const order = fullAutoBatch?.order || [];
    const ss = fullAutoBatch?.scraper_status || {};
    const done = order.filter((n) => terminalBatchJobStatus(ss[n])).length;
    const running = order.filter((n) => String(ss[n] || '').toLowerCase() === 'running').length;
    const batchPending = order.filter(
      (n) => String(ss[n] || 'pending').toLowerCase() === 'pending',
    ).length;
    const pending = Math.max(displayQueueItems.length, batchPending);
    const total = Math.max(order.length, done + running + pending);
    const pct = total ? Math.round((done / total) * 100) : 0;
    return { order, ss, done, running, pending, total, pct };
  }, [fullAutoBatch, displayQueueItems.length]);

  const queueOnlyScrapers = useMemo(() => {
    const inOrder = new Set(fullAutoBatch?.order || []);
    return displayQueueItems.filter((item) => !inOrder.has(item.scraper));
  }, [displayQueueItems, fullAutoBatch?.order]);

  const batchRunningScrapers = useMemo(() => {
    if (!fullAutoBatch?.order?.length) return [] as string[];
    const ss = fullAutoBatch.scraper_status || {};
    return fullAutoBatch.order.filter(
      (name) => String(ss[name] || '').toLowerCase() === 'running',
    );
  }, [fullAutoBatch]);

  /** Merge API progress with batch + heartbeat so Live Progress stays linked to the queue/batch. */
  const displayProgressMap = useMemo((): Record<string, ProgressInfo> => {
    const map: Record<string, ProgressInfo> = { ...progress.progress };

    const ensureStub = (runId: string, scraper: string, phase: string) => {
      if (map[runId]) return;
      map[runId] = {
        scraper,
        run_id: runId,
        started_at: new Date().toISOString(),
        status: 'running',
        phase,
        current_url_index: 0,
        total_urls: 0,
        events_scraped: 0,
        events_uploaded: 0,
        duplicates: 0,
        errors_count: 0,
        percent_complete: 0,
        updated_at: new Date().toISOString(),
      };
    };

    if (fullAutoBatch?.current_run_id && fullAutoBatch.current_scraper) {
      const rid = fullAutoBatch.current_run_id;
      const existing = map[rid];
      map[rid] = {
        ...(existing || {}),
        scraper: fullAutoBatch.current_scraper,
        run_id: rid,
        status: existing?.status || 'running',
        phase: existing?.phase || fullAutoBatch.last_message || 'Running in full automatic batch…',
        started_at: existing?.started_at || new Date().toISOString(),
        current_url_index: existing?.current_url_index ?? 0,
        total_urls: existing?.total_urls ?? 0,
        events_scraped: existing?.events_scraped ?? 0,
        events_uploaded: existing?.events_uploaded ?? 0,
        duplicates: existing?.duplicates ?? 0,
        errors_count: existing?.errors_count ?? 0,
        percent_complete: existing?.percent_complete ?? 0,
        updated_at: existing?.updated_at || new Date().toISOString(),
      };
    }

    for (const name of batchRunningScrapers) {
      const runId =
        fullAutoBatch?.current_scraper === name
          ? fullAutoBatch.current_run_id || undefined
          : localServerStatus?.runningScrapers?.[name] || undefined;
      if (runId) {
        ensureStub(runId, name, fullAutoBatch?.last_message || 'Running in batch — syncing stats…');
      }
    }

    for (const [scraper, runId] of Object.entries(localServerStatus?.runningScrapers || {})) {
      if (runId) {
        ensureStub(runId, scraper, 'Running on your PC — syncing live stats…');
      }
    }

    return map;
  }, [
    progress.progress,
    fullAutoBatch,
    batchRunningScrapers,
    localServerStatus?.runningScrapers,
  ]);

  const progressEntries = useMemo(() => Object.entries(displayProgressMap), [displayProgressMap]);
  const activeProgressEntries = useMemo(
    () => progressEntries.filter(([, p]) => !['completed', 'error', 'cancelled'].includes(p.status)),
    [progressEntries],
  );
  const finishedProgressEntries = useMemo(
    () => progressEntries.filter(([, p]) => ['completed', 'error', 'cancelled'].includes(p.status)),
    [progressEntries],
  );

  const activeScraperNamesFromProgress = useMemo(
    () => new Set(activeProgressEntries.map(([, p]) => p.scraper)),
    [activeProgressEntries],
  );

  const activeRunIdsFromProgress = useMemo(
    () => new Set(activeProgressEntries.map(([runId]) => runId)),
    [activeProgressEntries],
  );

  /** Minimal fallback cards only when batch/heartbeat says running but no progress row exists yet. */
  const offlineRunningEntries = useMemo(() => {
    const seen = new Set<string>();
    const out: Array<{ scraper: string; runId?: string; source: 'batch' | 'heartbeat' }> = [];

    const add = (scraper: string, runId: string | undefined, source: 'batch' | 'heartbeat') => {
      if (runId && activeRunIdsFromProgress.has(runId)) return;
      if (!runId && activeScraperNamesFromProgress.has(scraper)) return;
      const key = runId || scraper;
      if (seen.has(key)) return;
      seen.add(key);
      out.push({ scraper, runId, source });
    };

    for (const name of batchRunningScrapers) {
      const runId =
        fullAutoBatch?.current_scraper === name
          ? fullAutoBatch.current_run_id || undefined
          : localServerStatus?.runningScrapers?.[name] || undefined;
      add(name, runId, 'batch');
    }

    for (const [scraper, runId] of Object.entries(localServerStatus?.runningScrapers || {})) {
      add(scraper, runId || undefined, 'heartbeat');
    }

    return out;
  }, [
    batchRunningScrapers,
    fullAutoBatch?.current_scraper,
    fullAutoBatch?.current_run_id,
    localServerStatus?.runningScrapers,
    activeScraperNamesFromProgress,
    activeRunIdsFromProgress,
  ]);

  const totalActiveDisplayCount = Math.max(
    activeProgressEntries.length + offlineRunningEntries.length,
    batchRunningScrapers.length,
  );
  const batchRunningWithoutCards =
    batchRunningScrapers.length > activeProgressEntries.length
      ? batchRunningScrapers.length - activeProgressEntries.length
      : 0;
  const localProgressUnreachable =
    !progressLoading &&
    activeProgressEntries.every(([, p]) => p.events_scraped === 0 && p.total_urls === 0) &&
    (batchRunningWithoutCards > 0 || (localServerStatus?.runningCount ?? 0) > 0) &&
    (localServerStatus?.alive || localServerStatus?.watcherAlive);

  const doctorByScraper = useMemo(() => {
    const map = new Map<string, NonNullable<ScraperDoctorOverview['scrapers']>[number]>();
    for (const item of doctorOverview?.scrapers || []) {
      if (item?.name) map.set(item.name, item);
    }
    return map;
  }, [doctorOverview]);

  const doctorAttentionItems = useMemo(() => {
    const items = (doctorOverview?.scrapers || []).filter(
      (s) => s.needs_attention && s.enabled !== false,
    );
    // Worst first: last-run failures / 0% success before mild historical dips or never-ran.
    // Do NOT slice — a hard cap of 8 previously hid Hansen House, hangar_11, etc.
    return [...items].sort((a, b) => {
      const rank = (item: NonNullable<ScraperDoctorOverview['scrapers']>[number]) => {
        const sr = item.recent?.success_rate;
        const lastFailed =
          !!item.last_run_status &&
          !['success', 'completed', 'ok'].includes(String(item.last_run_status).toLowerCase());
        let tier = 4;
        if (lastFailed || (sr != null && sr <= 0)) tier = 0;
        else if (sr != null && sr < 30) tier = 1;
        else if (sr != null && sr < 70) tier = 2;
        else if (sr == null) tier = 3;
        return [tier, sr ?? 101, item.priority ?? 999, item.name || ''] as const;
      };
      const ra = rank(a);
      const rb = rank(b);
      for (let i = 0; i < ra.length; i += 1) {
        if (ra[i] < rb[i]) return -1;
        if (ra[i] > rb[i]) return 1;
      }
      return 0;
    });
  }, [doctorOverview]);

  const laneCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const item of doctorOverview?.scrapers || []) {
      const lane = item.lane || 'fast';
      counts[lane] = (counts[lane] || 0) + 1;
    }
    return counts;
  }, [doctorOverview]);

  const toggleScheduleSort = (column: ScheduleSortColumn) => {
    setScheduleSort((prev) => {
      if (prev.column === column) {
        return { column, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
      }
      return { column, dir: 'asc' };
    });
  };

  const apiScrapers: ScraperStatus[] = status?.scrapers ?? [];
  const byName = new Map<string, ScraperStatus>();
  LOCAL_ONLY_SCRAPERS.forEach((s) => byName.set(s.name, { ...s }));
  apiScrapers.forEach((s) => {
    if (!s?.name) return;
    const existing = byName.get(s.name);
    const merged = mergeOrchestrationRow(existing, s);
    byName.set(s.name, merged);
  });
  const combinedScrapers: ScraperStatus[] = Array.from(byName.values());
  const totalScrapersCount = combinedScrapers.length;

  const filteredSortedScheduleRows = useMemo(() => {
    const defaultCmp = (a: ScraperStatus, b: ScraperStatus): number => {
      const dp = displayPriority(a) - displayPriority(b);
      if (dp !== 0) return dp;
      return getScraperDisplayName(a.name).localeCompare(getScraperDisplayName(b.name), undefined, {
        sensitivity: 'base',
      });
    };

    const qName = nameFilter.trim().toLowerCase();
    const qLoc = headerLocationText.trim().toLowerCase();
    const qSched = headerScheduleText.trim().toLowerCase();

    let rows = combinedScrapers.filter((s: ScraperStatus) => s.enabled !== false);
    rows = rows.filter((s: ScraperStatus) => {
      if (locationFilter.length === 0) return true;
      const loc = getDisplayLocation(s);
      return locationFilter.includes(loc || '_none');
    });
    rows = rows.filter((s: ScraperStatus) => {
      if (!qName) return true;
      const display = getScraperDisplayName(s.name).toLowerCase();
      return s.name.toLowerCase().includes(qName) || display.includes(qName);
    });
    rows = rows.filter((s: ScraperStatus) => {
      if (!qLoc) return true;
      return (getDisplayLocation(s) || '').toLowerCase().includes(qLoc);
    });
    rows = rows.filter((s: ScraperStatus) => {
      if (!qSched) return true;
      return formatSchedule(s).toLowerCase().includes(qSched);
    });
    if (headerPriority !== '') {
      const p = Number(headerPriority);
      rows = rows.filter((s) => displayPriority(s) === p);
    }
    if (headerHealth) {
      rows = rows.filter((s) => {
        const st = health?.scrapers?.[s.name]?.status ?? 'unknown';
        return st === headerHealth;
      });
    }
    if (headerTrigger) {
      rows = rows.filter((s) => {
        const supportsLocal =
          LOCAL_ONLY_SCRAPER_NAMES_SET.has(s.name) || localRunnableNames.has(s.name);
        return getTriggerMode(s, supportsLocal) === headerTrigger;
      });
    }

    const col = scheduleSort.column;
    const dir = scheduleSort.dir;
    const mul = dir === 'asc' ? 1 : -1;

    if (!col) {
      return [...rows].sort(defaultCmp);
    }

    return [...rows].sort((a, b) => {
      if (col === 'lastRun') {
        const am = getLastSuccessfulRunMs(a, latestReports);
        const bm = getLastSuccessfulRunMs(b, latestReports);
        const aMiss = am == null;
        const bMiss = bm == null;
        let cmp: number;
        if (aMiss && bMiss) cmp = 0;
        else if (aMiss) cmp = 1;
        else if (bMiss) cmp = -1;
        else cmp = (am - bm) * mul;
        if (cmp !== 0) return cmp;
        return defaultCmp(a, b);
      }

      let c = 0;
      switch (col) {
        case 'name':
          c = getScraperDisplayName(a.name).localeCompare(getScraperDisplayName(b.name), undefined, {
            sensitivity: 'base',
          });
          break;
        case 'location':
          c = (getDisplayLocation(a) || '\uf8ff').localeCompare(getDisplayLocation(b) || '\uf8ff', undefined, {
            sensitivity: 'base',
          });
          break;
        case 'schedule':
          c = formatSchedule(a).localeCompare(formatSchedule(b), undefined, { sensitivity: 'base' });
          break;
        case 'priority':
          c = displayPriority(a) - displayPriority(b);
          break;
        case 'health': {
          const sa = health?.scrapers?.[a.name]?.status ?? 'unknown';
          const sb = health?.scrapers?.[b.name]?.status ?? 'unknown';
          c = healthRank(sa) - healthRank(sb);
          if (c === 0) c = sa.localeCompare(sb);
          break;
        }
        case 'trigger': {
          const sa = LOCAL_ONLY_SCRAPER_NAMES_SET.has(a.name) || localRunnableNames.has(a.name);
          const sb = LOCAL_ONLY_SCRAPER_NAMES_SET.has(b.name) || localRunnableNames.has(b.name);
          c = getTriggerLabel(getTriggerMode(a, sa)).localeCompare(
            getTriggerLabel(getTriggerMode(b, sb)),
            undefined,
            { sensitivity: 'base' }
          );
          break;
        }
        default:
          break;
      }
      if (c !== 0) return c * mul;
      return defaultCmp(a, b);
    });
  }, [
    combinedScrapers,
    locationFilter,
    nameFilter,
    headerLocationText,
    headerScheduleText,
    headerPriority,
    headerHealth,
    headerTrigger,
    scheduleSort,
    health,
    latestReports,
    localRunnableNames,
  ]);

  // Unique display locations for filter (includes "General, IL" for nationwide)
  return (
    <div className="scraper-orchestration">
      <h2>Scraper Orchestration</h2>
      
      {loading && (
        <div className="loading">Loading scraper orchestration data...</div>
      )}
      
      {/* Error Message */}
      {error && (
        <div className="error-message">
          <strong>⚠️ Error:</strong> {error}
          <br />
          <small>
            API Endpoint: https://scraping-machine-469675949347.europe-west1.run.app
            <br />
            Check browser console (F12) for detailed error information.
            <br />
            Common issues: CORS errors, network connectivity, or API service down.
          </small>
          <button onClick={loadData} className="retry-button">Retry</button>
        </div>
      )}
      
      {/* Overall Status */}
      <div className="status-card">
        <h3>System Status</h3>
        <div className="status-info">
          <p>
            Status: <span className={status?.status === 'healthy' ? 'status-healthy' : 'status-error'}>
              {status?.status || 'Unknown'}
            </span>
          </p>
          <p>Total Scrapers: {totalScrapersCount}</p>
          <p>Timezone: {status?.timezone || 'UTC'}</p>
          {status?.timestamp && (
            <p className="timestamp">Last Updated: {new Date(status.timestamp).toLocaleString()}</p>
          )}
        </div>
      </div>

      {/* Local server status & controls */}
      <div className="status-card local-server-card">
        <h3>Local scraper server</h3>
        <div className="local-server-actions">
          <div className="local-server-row">
            <span
              className={`local-server-indicator ${localServerStatus?.alive ? 'running' : localServerStatus === null ? 'checking' : 'stopped'}`}
              title={localServerStatus?.alive ? 'Server is running' : 'Server is offline'}
            />
            <span className="local-server-indicator-label">
              {localServerStatus === null
                ? 'Checking…'
                : localServerStatus.alive
                  ? `Running${localServerStatus.serverPort ? ` on :${localServerStatus.serverPort}` : ''}${localServerStatus.runningCount > 0 ? ` (${localServerStatus.runningCount} active)` : ''}`
                  : localServerStatus.watcherAlive
                    ? 'Offline (watcher ready)'
                    : 'Offline'}
            </span>
            <button
              type="button"
              className="server-btn start-btn"
              onClick={handleStartServer}
              disabled={localServerBusy || !!localServerStatus?.alive}
              title={
                localServerStatus?.alive
                  ? 'Server is already running'
                  : 'Start local scraper server (only needed if Full automatic did not start it)'
              }
            >
              {localServerStatus?.alive ? 'Server running' : 'Start server'}
            </button>
            <button
              type="button"
              className="server-btn stop-btn"
              onClick={() => handleServerCommand('stop')}
              disabled={localServerBusy || !localServerStatus?.alive}
              title="Stop local server"
            >
              Stop server
            </button>
            <button
              type="button"
              className="server-btn restart-btn"
              onClick={() => handleServerCommand('kill_and_restart')}
              disabled={localServerBusy || !localServerStatus?.alive}
              title="Cancel all running scrapers and restart the server"
            >
              Kill runs &amp; restart
            </button>
            <button
              type="button"
              className="server-btn automatic-btn"
              onClick={handleFullAutomaticLocalRun}
              disabled={localServerBusy || !localServerStatus?.watcherAlive}
              title="Starts the server if needed, then queues due local scrapers (spacing/cooldown). Safe to click once — do not spam Start server."
            >
              Full automatic scrape
            </button>
          </div>
          {localServerStatus?.firestorePermissionDenied && (
            <div className="local-server-startup-error">
              <p>
                <strong>Firestore rules need deploy.</strong> Your local server is reachable on localhost, but
                production rules are missing <code>local_server_*</code> collections — dashboard controls cannot
                read heartbeat or send commands.
              </p>
              <p className="local-server-startup-hint">
                One-time fix from repo root: <code>firebase deploy --only firestore:rules</code>
              </p>
            </div>
          )}
          {localServerStatus?.startupError?.active && (
            <div className="local-server-startup-error" role="alert">
              <strong>
                {localServerStatus.startupError.code === 'ZOMBIE_PORTS' ||
                localServerStatus.startupError.code === 'PORT_EXHAUSTED'
                  ? 'Local ports stuck'
                  : 'Local server could not start'}
              </strong>
              <p>{localServerStatus.startupError.message}</p>
              {localServerStatus.startupError.instructions && (
                <p className="local-server-startup-instructions">
                  {localServerStatus.startupError.instructions}
                </p>
              )}
              {localServerStatus.startupError.autoFixable && localServerStatus.watcherAlive && (
                <p className="local-server-startup-hint">
                  The watcher will retry automatically. You can also use <strong>Kill runs &amp; restart</strong>.
                </p>
              )}
            </div>
          )}
          {localServerMsg && (
            <p className={`local-server-msg ${localServerMsg.type}`}>{localServerMsg.text}</p>
          )}
          {!localServerStatus?.alive && !localServerStatus?.watcherAlive && localServerStatus !== null && (
            <p className="local-server-hint">To control from this dashboard, run on your PC once: <code>python local_server_watcher.py --install</code></p>
          )}
          {localServerStatus?.watcherAlive && !localServerStatus?.alive && !localServerStatus?.startupError?.active && (
            <p className="local-server-hint">
              Use <strong>Full automatic scrape</strong> to start the server and queue scrapers in one step.
              Avoid clicking Start server repeatedly.
            </p>
          )}
        </div>
      </div>

      {/* Live Progress Section - Always visible */}
      <div className="progress-section" id="live-progress-section">
        <div className="progress-section-header">
          <h3>
            Live Progress{' '}
            {totalActiveDisplayCount > 0 && (
              <span style={{ color: '#28a745', fontSize: '0.9rem' }}>({totalActiveDisplayCount} active)</span>
            )}
            {displayQueueItems.length > 0 && (
              <span style={{ color: '#856404', fontSize: '0.9rem', marginLeft: '0.5rem' }}>
                ({displayQueueItems.length} queued)
              </span>
            )}
          </h3>
          <button
            type="button"
            className="server-btn stop-scraping-btn"
            onClick={handleStopScraping}
            disabled={localServerBusy || !localServerStatus?.watcherAlive}
            title="Pause scheduled automation, cancel queued batch jobs, and signal current runs to stop"
          >
            Stop scraping
          </button>
        </div>
        {fullAutoBatch && fullAutoBatchId && (
          <div className="live-progress-batch-link">
            <div className="live-progress-batch-link-main">
              <strong>Batch {fullAutoBatch.batch_id || fullAutoBatchId}</strong>
              <span className={`full-auto-status-pill status-${fullAutoBatch.status || 'unknown'}`}>
                {fullAutoBatch.status || '—'}
              </span>
              {fullAutoProgressStats.total > 0 && (
                <span className="live-progress-batch-progress">
                  {fullAutoProgressStats.done} / {fullAutoProgressStats.total} scrapers done
                </span>
              )}
            </div>
            {fullAutoBatch.current_scraper && (
              <p className="live-progress-batch-now">
                Now running: <strong>{getScraperDisplayName(fullAutoBatch.current_scraper)}</strong>
                {fullAutoBatch.current_run_id && (
                  <span className="live-progress-batch-run-id"> · run {fullAutoBatch.current_run_id.slice(0, 8)}</span>
                )}
              </p>
            )}
            {fullAutoBatch.last_message && (
              <p className="live-progress-batch-msg">{fullAutoBatch.last_message}</p>
            )}
            <p className="live-progress-batch-hint">
              Queue below is the same jobs as the batch panel — cards update via Firestore sync from your PC.
            </p>
          </div>
        )}
        {localProgressUnreachable && (
          <p className="progress-local-hint progress-local-unreachable">
            <strong>{batchRunningWithoutCards} batch run(s) active on your PC</strong> — waiting for progress sync
            (Firestore heartbeat or localhost). Restart <code>local_scraper_server.py</code> once if stats stay empty.
            Batch table below is still live via Firestore.
          </p>
        )}
        {progress.count === 0 &&
          !progressLoading &&
          !localProgressUnreachable &&
          batchRunningScrapers.length === 0 &&
          (localServerStatus?.alive || localServerStatus?.watcherAlive) && (
            <p className="progress-local-hint">
              Runs on your PC appear here only if this browser can open <code>http://localhost:8101</code> (or 8102/8103).
              Use the dashboard on the same machine as <code>local_server_watcher.py</code>.
            </p>
          )}

        {progressError && (
          <div className="error-message" style={{ marginBottom: '1rem' }}>
            <strong>⚠️ Progress Error:</strong> {progressError}
          </div>
        )}
        {displayQueueItems.length > 0 && (
          <div className="pending-queue-section">
            <div className="pending-queue-header">
              <h4>Queued (waiting for slot)</h4>
              {displayQueueItems.length > 8 && (
                <button
                  type="button"
                  className="full-auto-toggle-details"
                  onClick={() => setPendingQueueExpanded((v) => !v)}
                >
                  {pendingQueueExpanded ? 'Show less' : `Show all ${displayQueueItems.length}`}
                </button>
              )}
            </div>
            <p className="pending-queue-hint">
              {fullAutoBatchId
                ? `Full automatic batch ${(fullAutoBatch?.batch_id || fullAutoBatchId).slice(0, 12)} — jobs start in order when a lane slot opens.`
                : 'One local scrape at a time — these start automatically in order.'}
            </p>
            <div className="compact-queue-table-wrap">
              <table className="compact-queue-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Scraper</th>
                    {fullAutoBatch?.order?.length ? <th>Batch slot</th> : null}
                    <th>Requested</th>
                  </tr>
                </thead>
                <tbody>
                  {(pendingQueueExpanded ? displayQueueItems : displayQueueItems.slice(0, 8)).map(
                    (item, index) => {
                      const batchSlot = fullAutoBatch?.order?.indexOf(item.scraper);
                      return (
                      <tr key={item.id}>
                        <td>{index + 1}</td>
                        <td>{getScraperDisplayName(item.scraper)}</td>
                        {fullAutoBatch?.order?.length ? (
                          <td>{batchSlot != null && batchSlot >= 0 ? batchSlot + 1 : '—'}</td>
                        ) : null}
                        <td>
                          {item.requested_at
                            ? new Date(item.requested_at).toLocaleString()
                            : '—'}
                        </td>
                      </tr>
                    );},
                  )}
                </tbody>
              </table>
            </div>
            {!pendingQueueExpanded && displayQueueItems.length > 8 && (
              <p className="pending-queue-more">
                + {displayQueueItems.length - 8} more in queue
              </p>
            )}
          </div>
        )}
        {progressLoading ? (
          <div className="loading" style={{ padding: '1rem', background: '#fff3cd' }}>Loading progress data...</div>
        ) : activeProgressEntries.length > 0 || offlineRunningEntries.length > 0 ? (
          <div className="progress-cards">
            {activeProgressEntries.map(([runId, prog]: [string, ProgressInfo]) => {
              const isActive = !['completed', 'error', 'cancelled'].includes(prog.status);
              const isBatchCurrent = fullAutoBatch?.current_run_id === runId;
              const elapsed = prog.duration_seconds || 
                (new Date().getTime() - new Date(prog.started_at).getTime()) / 1000;
              const isCancelling = cancelling.has(runId);
              const startTime = new Date(prog.started_at);
              const endTime = prog.completed_at ? new Date(prog.completed_at) : new Date();
              const eventLinkState = buildEventDashboardLinkState(prog.scraper, startTime, endTime);
              
              return (
                <div
                  key={runId}
                  className={`progress-card ${prog.status}${isBatchCurrent ? ' progress-card-batch-current' : ''}`}
                >
                  <div className="progress-header">
                    <span className="scraper-name">{getScraperDisplayName(prog.scraper)}</span>
                    <span className="run-id">Run: {runId}</span>
                    {isBatchCurrent && (
                      <span className="progress-batch-badge">Batch active</span>
                    )}
                    <span className={`status-badge ${prog.status}`}>
                      {prog.status === 'running' ? '🟢 Running' :
                       prog.status === 'cancelling' ? '⏳ Cancelling…' :
                       prog.status === 'completed' ? '✅ Completed' :
                       prog.status === 'cancelled' ? '🚫 Cancelled' :
                       prog.status === 'error' ? '❌ Error' : prog.status}
                    </span>
                  </div>
                  
                  <div className="progress-time-info">
                    <div className="start-time">
                      <strong>Started:</strong> {startTime.toLocaleString()}
                    </div>
                    {prog.completed_at && (
                      <div className="end-time">
                        <strong>Completed:</strong> {new Date(prog.completed_at).toLocaleString()}
                      </div>
                    )}
                  </div>
                  
                  <div className="progress-phase">
                    <strong>Phase:</strong> {prog.phase}
                  </div>
                  
                  {prog.total_urls > 0 ? (
                    <div className="progress-bar-container">
                      <div className="progress-bar-label">
                        URLs: {prog.current_url_index} / {prog.total_urls} ({Number(prog.percent_complete || 0).toFixed(1)}%)
                      </div>
                      <div className="progress-bar">
                        <div 
                          className="progress-bar-fill" 
                          style={{ width: `${Number(prog.percent_complete || 0)}%` }}
                        />
                      </div>
                    </div>
                  ) : isActive && prog.events_scraped > 0 ? (
                    <div className="progress-bar-container">
                      <div className="progress-bar-label">
                        Events collected: {prog.events_scraped}
                        {prog.events_uploaded > 0 ? ` · uploaded ${prog.events_uploaded}` : ''}
                      </div>
                    </div>
                  ) : null}
                  
                  <div className="progress-stats">
                    <div className="stat-item">
                      <span className="stat-label">Events Scraped:</span>
                      <span className="stat-value">{prog.events_scraped}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Events Uploaded:</span>
                      <span className="stat-value">{prog.events_uploaded}</span>
                    </div>
                    {prog.duplicates > 0 && (
                      <div className="stat-item">
                        <span className="stat-label">Duplicates:</span>
                        <span className="stat-value">{prog.duplicates}</span>
                      </div>
                    )}
                    {prog.errors_count > 0 && (
                      <div className="stat-item error">
                        <span className="stat-label">Errors:</span>
                        <span className="stat-value">{prog.errors_count}</span>
                      </div>
                    )}
                  </div>
                  
                  {prog.last_event_title && (
                    <div className="last-event">
                      <strong>Last Event:</strong> {prog.last_event_title}
                    </div>
                  )}
                  
                  <div className="progress-time">
                    <span>Elapsed: {formatDuration(elapsed)}</span>
                    {prog.estimated_remaining_seconds && isActive && (
                      <span>Est. Remaining: {formatDuration(prog.estimated_remaining_seconds)}</span>
                    )}
                  </div>
                  
                  {prog.current_item && (
                    <div className="current-item">
                      <small>Processing: {prog.current_item}</small>
                    </div>
                  )}
                  
                  <div className="progress-actions">
                    {prog.status === 'completed' && prog.events_uploaded > 0 && (
                      <Link 
                        to="/admin"
                        state={eventLinkState}
                        className="btn-view-events"
                      >
                        📅 View Events from this Scrape
                      </Link>
                    )}
                    {isActive && (
                      <button
                        onClick={() => handleCancelScraper(runId, prog.scraper)}
                        className="btn-cancel"
                        disabled={isCancelling}
                      >
                        {isCancelling ? 'Cancelling...' : '🛑 Cancel Scrape'}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
            {offlineRunningEntries.map(({ scraper, runId, source }) => (
              <div key={`offline-${scraper}-${runId || source}`} className="progress-card running progress-card-offline">
                <div className="progress-header">
                  <span className="scraper-name">{getScraperDisplayName(scraper)}</span>
                  {runId && <span className="run-id">Run: {runId}</span>}
                  <span className="status-badge running">🟢 Running</span>
                </div>
                <div className="progress-phase">
                  <strong>Source:</strong> {source === 'batch' ? 'Full automatic batch (Firestore)' : 'Local server heartbeat'}
                </div>
                <p className="progress-offline-note">
                  URL/event counters need localhost — scrape is still running on your PC.
                </p>
              </div>
            ))}
          </div>
        ) : null}
        {finishedProgressEntries.length > 0 && (
          <div className="finished-runs-section">
            <button
              type="button"
              className="full-auto-toggle-details"
              onClick={() => setCompletedRunsExpanded((v) => !v)}
            >
              {completedRunsExpanded
                ? 'Hide finished runs'
                : `Show ${finishedProgressEntries.length} finished run(s)`}
            </button>
            {completedRunsExpanded && (
              <div className="finished-runs-list">
                {finishedProgressEntries.map(([runId, prog]) => (
                  <div key={runId} className={`finished-run-row status-${prog.status}`}>
                    <span className="finished-run-scraper">{prog.scraper}</span>
                    <span className="finished-run-id">{runId}</span>
                    <span className={`finished-run-status ${prog.status}`}>{prog.status}</span>
                    <span className="finished-run-phase" title={prog.phase}>
                      {prog.phase}
                    </span>
                    <span className="finished-run-stats-inline">
                      ↑{prog.events_uploaded} / {prog.events_scraped}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        {!progressLoading &&
          activeProgressEntries.length === 0 &&
          offlineRunningEntries.length === 0 &&
          batchRunningScrapers.length === 0 &&
          finishedProgressEntries.length === 0 && (
          <div className="no-progress-message" style={{ 
            padding: '2rem', 
            textAlign: 'center', 
            color: '#666',
            background: '#f8f9fa',
            borderRadius: '8px',
            border: '2px dashed #dee2e6'
          }}>
            <p style={{ margin: 0, fontSize: '1rem' }}>
              {displayQueueItems.length > 0
                ? '📊 No scraper running right now — see queued jobs above'
                : batchRunningScrapers.length > 0
                  ? `📊 ${batchRunningScrapers.length} scraper(s) running in batch — sync pending`
                  : '📊 No active scrapers at the moment'}
            </p>
            <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.875rem', color: '#999' }}>
              {displayQueueItems.length > 0
                ? 'Queued scrapers start automatically when the current slot is free'
                : 'Progress will appear here when scrapers are running'}
            </p>
          </div>
        )}
      </div>

      {(fullAutoBatchId || fullAutoBatch) && (
        <div className="status-card full-auto-batch-card">
          <div className="full-auto-batch-header">
            <h3>Full automatic scrape — live batch</h3>
            <div className="full-auto-batch-header-actions">
              <button
                type="button"
                className="server-btn stop-scraping-btn"
                onClick={handleStopScraping}
                disabled={localServerBusy || !localServerStatus?.watcherAlive}
                title="Pause automation, cancel queued batch jobs, and signal active runs to stop cooperatively"
              >
                Stop scraping
              </button>
              <button
                type="button"
                className="full-auto-dismiss"
                onClick={() => {
                  setFullAutoBatchId(null);
                  setFullAutoBatch(null);
                }}
              >
                Dismiss
              </button>
            </div>
          </div>
          {!fullAutoBatch ? (
            <p className="full-auto-loading">
              Loading batch <code>{fullAutoBatchId}</code>…
            </p>
          ) : (
            <>
              <p className="full-auto-meta">
                <span className="full-auto-batch-id">Batch {fullAutoBatch.batch_id || fullAutoBatchId}</span>
                <span className={`full-auto-status-pill status-${fullAutoBatch.status || 'unknown'}`}>
                  {fullAutoBatch.status || '—'}
                </span>
                {fullAutoBatch.started_at && (
                  <span className="full-auto-started">
                    Started {new Date(fullAutoBatch.started_at).toLocaleString()}
                  </span>
                )}
              </p>
              {fullAutoBatch.last_message && (
                <p className="full-auto-last-msg">{fullAutoBatch.last_message}</p>
              )}
              {(fullAutoBatch.order || []).length === 0 && (
                <p className="full-auto-empty-order">
                  Nothing was queued in this batch. Only scrapers with trigger <strong>Scheduled local</strong> that are
                  actually <em>due</em> (by schedule, spacing, and preferred run time) are included.
                </p>
              )}
              {fullAutoBatch.status === 'running' &&
                (fullAutoBatch.total ?? 0) > 0 &&
                progress.count === 0 &&
                localServerStatus?.alive && (
                  <p className="full-auto-live-hint">
                    Detailed run cards show under <strong>Live Progress</strong> when this browser can reach your local
                    scraper server (same PC as the watcher; ports 8101–8103).
                  </p>
                )}
              {(() => {
                const { order, ss, done, pending, total, pct } = fullAutoProgressStats;
                const autoExpand = total > 12;
                const statusCounts: Record<string, number> = {};
                for (const name of order) {
                  const st = String(ss[name] || 'pending').toLowerCase();
                  statusCounts[st] = (statusCounts[st] || 0) + 1;
                }
                if (pending > (statusCounts.pending ?? 0)) {
                  statusCounts.pending = pending;
                }
                return (
                  <div className="full-auto-summary">
                    <div className="full-auto-bar-label">
                      {done} / {total} scrapers finished ({pct}%)
                      {displayQueueItems.length > 0 && (
                        <span className="full-auto-queue-inline">
                          {' '}
                          · {displayQueueItems.length} in queue
                        </span>
                      )}
                    </div>
                    <div className="full-auto-bar">
                      <div className="full-auto-bar-fill" style={{ width: `${pct}%` }} />
                    </div>
                    {Object.keys(statusCounts).length > 0 && (
                      <div className="full-auto-status-chips">
                        {Object.entries(statusCounts).map(([st, n]) => (
                          <span key={st} className={`full-auto-chip st-${st}`}>
                            {st}: {n}
                          </span>
                        ))}
                      </div>
                    )}
                    {autoExpand && !fullAutoTableExpanded && pending > 0 && (
                      <p className="full-auto-compact-hint">
                        {pending} pending — expand the table below to see the full queue list.
                      </p>
                    )}
                  </div>
                );
              })()}
              {fullAutoBatch.current_run_id && (
                <div className="full-auto-current-run">
                  <h4>
                    Active run: {getScraperDisplayName(fullAutoBatch.current_scraper || '')}
                    {' '}
                    <a href="#live-progress-section" className="full-auto-jump-live">
                      View in Live Progress ↑
                    </a>
                  </h4>
                  {displayProgressMap[fullAutoBatch.current_run_id] ? (
                    (() => {
                      const prog = displayProgressMap[fullAutoBatch.current_run_id!];
                      const elapsed =
                        prog.duration_seconds ||
                        (new Date().getTime() - new Date(prog.started_at).getTime()) / 1000;
                      return (
                        <ul className="full-auto-run-stats">
                          <li>
                            <strong>Phase</strong> {prog.phase}
                          </li>
                          <li>
                            <strong>Status</strong> {prog.status}
                          </li>
                          {prog.total_urls > 0 && (
                            <li>
                              <strong>URLs</strong> {prog.current_url_index} / {prog.total_urls} (
                              {Number(prog.percent_complete || 0).toFixed(1)}%)
                            </li>
                          )}
                          <li>
                            <strong>Events</strong> scraped {prog.events_scraped}, uploaded{' '}
                            {prog.events_uploaded}
                            {prog.duplicates > 0 && `, dup ${prog.duplicates}`}
                          </li>
                          <li>
                            <strong>Elapsed</strong> {formatDuration(elapsed)}
                          </li>
                          {prog.current_item && (
                            <li>
                              <strong>Now</strong> {prog.current_item}
                            </li>
                          )}
                        </ul>
                      );
                    })()
                  ) : (
                    <p className="full-auto-waiting">Waiting for live progress (poll sync)…</p>
                  )}
                </div>
              )}
              <div className="full-auto-table-wrap">
                {(fullAutoBatch.order || []).length > 12 && (
                  <button
                    type="button"
                    className="full-auto-toggle-details"
                    onClick={() => setFullAutoTableExpanded((v) => !v)}
                  >
                    {fullAutoTableExpanded
                      ? 'Hide full scraper list'
                      : `Show all ${(fullAutoBatch.order || []).length} scrapers`}
                  </button>
                )}
                <div
                  className={`full-auto-table-scroll ${fullAutoTableExpanded ? 'expanded' : 'compact'}`}
                >
                  <table className="full-auto-table">
                    <thead>
                      <tr>
                        <th>Scraper</th>
                        <th>Status</th>
                        <th>Error / note</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(fullAutoTableExpanded || (fullAutoBatch.order || []).length <= 12
                        ? fullAutoBatch.order || []
                        : (fullAutoBatch.order || []).filter((name) => {
                            const st = String(
                              (fullAutoBatch.scraper_status || {})[name] || 'pending',
                            ).toLowerCase();
                            return st === 'pending' || st === 'running' || st === 'error';
                          })
                      ).map((name) => {
                        const st = (fullAutoBatch.scraper_status || {})[name] || 'pending';
                        const err = (fullAutoBatch.scraper_errors || {})[name];
                        return (
                          <tr key={name} className={st === 'running' ? 'full-auto-row-active' : ''}>
                            <td>{getScraperDisplayName(name)}</td>
                            <td>
                              <span className={`full-auto-job-status st-${st}`}>{st}</span>
                            </td>
                            <td className="full-auto-job-note">{err || '—'}</td>
                          </tr>
                        );
                      })}
                      {queueOnlyScrapers.map((item) => (
                        <tr key={`queue-${item.id}`} className="full-auto-row-queued">
                          <td>{getScraperDisplayName(item.scraper)}</td>
                          <td>
                            <span className="full-auto-job-status st-pending">queued</span>
                          </td>
                          <td className="full-auto-job-note">In Firestore queue (batch doc out of sync)</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      <div className="status-card scraper-doctor-card">
        <div className="scraper-doctor-header">
          <div>
            <h3>Scraper Doctor</h3>
            <p className="scraper-doctor-subtitle">
              Review code, run a test scrape, classify failures, then fix. Long scrapers run in separate lanes.
            </p>
          </div>
          <button type="button" className="doctor-refresh-btn" onClick={refreshDoctor} disabled={doctorRefreshing}>
            {doctorRefreshing ? 'Refreshing…' : 'Refresh doctor'}
          </button>
        </div>
        <div className="doctor-metrics-grid">
          <div className="doctor-metric">
            <span className="doctor-metric-label">Needs attention</span>
            <strong>{doctorOverview?.needs_attention_count ?? doctorAttentionItems.length}</strong>
          </div>
          <div className="doctor-metric">
            <span className="doctor-metric-label">Active local runs</span>
            <strong>{doctorOverview?.concurrency?.total_active ?? activeProgressEntries.length}</strong>
          </div>
          <div className="doctor-metric">
            <span className="doctor-metric-label">Max simultaneous</span>
            <strong>{doctorOverview?.concurrency?.max_total_concurrent ?? doctorOverview?.max_total_concurrent ?? fullAutoBatch?.max_total_concurrent ?? 2}</strong>
          </div>
          <div className="doctor-metric">
            <span className="doctor-metric-label">Lane limits</span>
            <strong>{formatLaneLimits(effectiveLaneLimits)}</strong>
          </div>
        </div>
        <div className="doctor-lanes">
          {Object.entries(laneCounts).map(([lane, count]) => (
            <span key={lane} className={`doctor-lane-pill lane-${lane}`}>
              {lane}: {count}
            </span>
          ))}
        </div>
        {doctorAttentionItems.length > 0 ? (
          <div className="doctor-attention-list">
            <p className="doctor-attention-count-hint">
              Showing all {doctorAttentionItems.length} scrapers needing attention (worst first).
            </p>
            {doctorAttentionItems.map((item) => (
              <div key={item.name} className="doctor-attention-item">
                <div>
                  <strong>{getScraperDisplayName(item.name)}</strong>
                  <span>
                    {item.lane || 'fast'} lane · {item.last_failure_class || item.last_run_status || 'no recent success'}
                    {item.recent?.success_rate != null ? ` · ${item.recent.success_rate}% success` : ''}
                  </span>
                </div>
                <div className="doctor-attention-actions">
                  <button type="button" onClick={() => handleDoctorReview(item.name)} disabled={!!doctorBusy}>
                    Review
                  </button>
                  <button type="button" onClick={() => handleDoctorReview(item.name, true)} disabled={!!doctorBusy || !localRunnableNames.has(item.name)}>
                    Test
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="doctor-empty">No urgent scraper incidents found in the current Doctor overview.</p>
        )}
        {doctorReviewOpen && doctorReview && (
          <div className="doctor-review-panel">
            <div className="doctor-review-header">
              <h4>Review: {getScraperDisplayName(doctorReview.scraper)}</h4>
              <button type="button" onClick={() => setDoctorReviewOpen(false)}>Close</button>
            </div>
            <div className="doctor-review-meta">
              <span>Lane: {doctorReview.lane || '—'}</span>
              <span>Import: {doctorReview.import_check?.ok ? 'ok' : doctorReview.import_check?.error || 'unknown'}</span>
              <span>Recent runs: {doctorReview.recent?.recent_runs ?? 0}</span>
              <span>Median duration: {doctorReview.recent?.median_duration_seconds ?? '—'}s</span>
            </div>
            {doctorReview.findings && doctorReview.findings.length > 0 && (
              <ul className="doctor-findings">
                {doctorReview.findings.map((finding, idx) => (
                  <li key={`${finding.kind}-${idx}`} className={`finding-${finding.severity}`}>
                    <strong>{finding.severity}</strong> {finding.kind}: {finding.message}
                  </li>
                ))}
              </ul>
            )}
            {doctorReview.failure_classes && doctorReview.failure_classes.length > 0 && (
              <p className="doctor-failure-classes">
                Failure classes: {doctorReview.failure_classes.join(', ')}
              </p>
            )}
            <p className="doctor-next-step">{doctorReview.next_step || 'Run a test scrape before editing code.'}</p>
          </div>
        )}
      </div>

      {/* Scrapers Table */}
      <div className="scrapers-table-container">
        <h3>Scrapers Schedule</h3>
        <p className="table-hint" style={{ margin: '0 0 0.75rem 0', fontSize: '0.875rem', color: '#555' }}>
          If a scraper is missing, set <strong>Location</strong> to <strong>All</strong> (not a subset), or filter by name in the <strong>Scraper</strong> column header.
        </p>
        <div className="table-filter" style={{ marginBottom: '0.75rem', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.5rem 1rem' }}>
          <span style={{ fontWeight: 500 }}>Location:</span>
          <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={locationFilter.length === 0}
              onChange={(e) => { if (e.target.checked) setLocationFilter([]); }}
            />
            All
          </label>
          {Array.from(new Set(combinedScrapers.map(s => getDisplayLocation(s)).filter(Boolean))).sort().map(loc => (
            <label key={loc} style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={locationFilter.includes(loc)}
                onChange={(e) => {
                  if (e.target.checked) setLocationFilter(prev => [...prev, loc]);
                  else setLocationFilter(prev => prev.filter(x => x !== loc));
                }}
              />
              {loc}
            </label>
          ))}
          {combinedScrapers.some(s => !getDisplayLocation(s)) && (
            <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={locationFilter.includes('_none')}
                onChange={(e) => {
                  if (e.target.checked) setLocationFilter(prev => [...prev, '_none']);
                  else setLocationFilter(prev => prev.filter(x => x !== '_none'));
                }}
              />
              No location
            </label>
          )}
        </div>
        <div className="table-wrapper">
          <table className="scrapers-table">
            <thead>
              <tr>
                <th
                  scope="col"
                  aria-sort={
                    scheduleSort.column === 'name'
                      ? scheduleSort.dir === 'asc'
                        ? 'ascending'
                        : 'descending'
                      : undefined
                  }
                >
                  <button type="button" className="th-sort-btn" onClick={() => toggleScheduleSort('name')}>
                    Scraper
                    {scheduleSort.column === 'name' ? (scheduleSort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
                  </button>
                </th>
                <th
                  scope="col"
                  aria-sort={
                    scheduleSort.column === 'location'
                      ? scheduleSort.dir === 'asc'
                        ? 'ascending'
                        : 'descending'
                      : undefined
                  }
                >
                  <button type="button" className="th-sort-btn" onClick={() => toggleScheduleSort('location')}>
                    Location
                    {scheduleSort.column === 'location' ? (scheduleSort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
                  </button>
                </th>
                <th
                  scope="col"
                  aria-sort={
                    scheduleSort.column === 'schedule'
                      ? scheduleSort.dir === 'asc'
                        ? 'ascending'
                        : 'descending'
                      : undefined
                  }
                >
                  <button type="button" className="th-sort-btn" onClick={() => toggleScheduleSort('schedule')}>
                    Schedule
                    {scheduleSort.column === 'schedule' ? (scheduleSort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
                  </button>
                </th>
                <th
                  scope="col"
                  aria-sort={
                    scheduleSort.column === 'lastRun'
                      ? scheduleSort.dir === 'asc'
                        ? 'ascending'
                        : 'descending'
                      : undefined
                  }
                >
                  <button type="button" className="th-sort-btn" onClick={() => toggleScheduleSort('lastRun')}>
                    Last successful run
                    {scheduleSort.column === 'lastRun' ? (scheduleSort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
                  </button>
                </th>
                <th
                  scope="col"
                  aria-sort={
                    scheduleSort.column === 'priority'
                      ? scheduleSort.dir === 'asc'
                        ? 'ascending'
                        : 'descending'
                      : undefined
                  }
                >
                  <button type="button" className="th-sort-btn" onClick={() => toggleScheduleSort('priority')}>
                    Priority
                    {scheduleSort.column === 'priority' ? (scheduleSort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
                  </button>
                </th>
                <th
                  scope="col"
                  aria-sort={
                    scheduleSort.column === 'health'
                      ? scheduleSort.dir === 'asc'
                        ? 'ascending'
                        : 'descending'
                      : undefined
                  }
                >
                  <button type="button" className="th-sort-btn" onClick={() => toggleScheduleSort('health')}>
                    Health
                    {scheduleSort.column === 'health' ? (scheduleSort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
                  </button>
                </th>
                <th
                  scope="col"
                  aria-sort={
                    scheduleSort.column === 'trigger'
                      ? scheduleSort.dir === 'asc'
                        ? 'ascending'
                        : 'descending'
                      : undefined
                  }
                >
                  <button type="button" className="th-sort-btn" onClick={() => toggleScheduleSort('trigger')}>
                    Trigger
                    {scheduleSort.column === 'trigger' ? (scheduleSort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
                  </button>
                </th>
                <th scope="col">Actions</th>
              </tr>
              <tr className="scrapers-table-filter-row">
                <th>
                  <input
                    type="search"
                    className="th-filter-control"
                    placeholder="Name…"
                    value={nameFilter}
                    onChange={(e) => setNameFilter(e.target.value)}
                    aria-label="Filter by scraper name"
                  />
                </th>
                <th>
                  <input
                    type="search"
                    className="th-filter-control"
                    placeholder="Contains…"
                    value={headerLocationText}
                    onChange={(e) => setHeaderLocationText(e.target.value)}
                    aria-label="Filter by location text"
                  />
                </th>
                <th>
                  <input
                    type="search"
                    className="th-filter-control"
                    placeholder="Contains…"
                    value={headerScheduleText}
                    onChange={(e) => setHeaderScheduleText(e.target.value)}
                    aria-label="Filter by schedule text"
                  />
                </th>
                <th className="th-filter-spacer" />
                <th>
                  <select
                    className="th-filter-control"
                    value={headerPriority}
                    onChange={(e) => setHeaderPriority(e.target.value)}
                    aria-label="Filter by priority"
                  >
                    <option value="">All</option>
                    <option value="1">1</option>
                    <option value="2">2</option>
                    <option value="3">3</option>
                    <option value="4">4</option>
                    <option value="5">5</option>
                  </select>
                </th>
                <th>
                  <select
                    className="th-filter-control"
                    value={headerHealth}
                    onChange={(e) => setHeaderHealth(e.target.value)}
                    aria-label="Filter by health"
                  >
                    <option value="">All</option>
                    <option value="healthy">healthy</option>
                    <option value="degraded">degraded</option>
                    <option value="unknown">unknown</option>
                    <option value="unhealthy">unhealthy</option>
                  </select>
                </th>
                <th>
                  <select
                    className="th-filter-control"
                    value={headerTrigger}
                    onChange={(e) => setHeaderTrigger(e.target.value)}
                    aria-label="Filter by trigger"
                  >
                    <option value="">All</option>
                    <option value="scheduled_local">Scheduled local</option>
                    <option value="manual_local">Manual local</option>
                    <option value="cloud_run">Cloud Run</option>
                  </select>
                </th>
                <th className="th-filter-spacer" />
              </tr>
            </thead>
            <tbody>
              {filteredSortedScheduleRows.map((scraper: ScraperStatus) => {
                const healthInfo = health?.scrapers?.[scraper.name];
                const isFullRunBusy = triggering.has(scraper.name);
                const supportsLocal =
                  LOCAL_ONLY_SCRAPER_NAMES_SET.has(scraper.name) || localRunnableNames.has(scraper.name);
                const triggerMode = getTriggerMode(scraper, supportsLocal);
                const priorityForDisplay = displayPriority(scraper);
                const latestReportForScraper = latestReports?.[scraper.name];
                const latestCompletedIso =
                  scraper.last_successful_run ||
                  (latestReportForScraper && latestReportForScraper.status === 'completed'
                    ? (latestReportForScraper.end_time || latestReportForScraper.start_time)
                    : undefined);
                const lastSuccess = latestCompletedIso ? new Date(latestCompletedIso).toLocaleString() : '—';
                const isLate = Boolean(scraper.is_late);
                const doctorItem = doctorByScraper.get(scraper.name);
                return (
                  <tr key={scraper.name}>
                    <td className="scraper-name">
                      {getScraperDisplayName(scraper.name)}
                      {doctorItem && (
                        <div className="table-doctor-meta">
                          <span className={`doctor-lane-mini lane-${doctorItem.lane || 'fast'}`}>
                            {doctorItem.lane || 'fast'}
                          </span>
                          {doctorItem.needs_attention && (
                            <span className="doctor-attention-mini">
                              {doctorItem.last_failure_class || 'needs attention'}
                            </span>
                          )}
                        </div>
                      )}
                    </td>
                    <td>{getDisplayLocation(scraper) || '—'}</td>
                    <td>{formatSchedule(scraper)}</td>
                    <td>
                      <span className={isLate ? 'last-successful-run late' : 'last-successful-run'}>
                        {lastSuccess}
                      </span>
                    </td>
                    <td>
                      <span className={`priority-badge priority-${priorityForDisplay}`}>
                        {priorityForDisplay}
                      </span>
                    </td>
                    <td>
                      {healthInfo ? (
                        <span className={`health-badge ${healthInfo.status === 'healthy' ? 'healthy' : healthInfo.status === 'degraded' ? 'degraded' : healthInfo.status === 'unknown' ? 'unknown' : 'unhealthy'}`}>
                          {healthInfo.status}
                        </span>
                      ) : (
                        <span className="health-badge unknown">Unknown</span>
                      )}
                    </td>
                    <td>
                      <span className={`trigger-badge ${getTriggerClassName(triggerMode)}`}>
                        {getTriggerLabel(triggerMode)}
                      </span>
                    </td>
                    <td>
                      <div className="run-actions">
                        <button
                          type="button"
                          onClick={() => handleRunScraper(scraper.name, supportsLocal ? 'manual_local' : 'cloud')}
                          className="btn-run"
                          disabled={isFullRunBusy || !scraper.enabled}
                        >
                          {isFullRunBusy ? 'Running…' : 'Run now'}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDoctorReview(scraper.name)}
                          className="btn-doctor"
                          disabled={!!doctorBusy}
                        >
                          Review
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDoctorReview(scraper.name, true)}
                          className="btn-doctor-test"
                          disabled={!!doctorBusy || !supportsLocal}
                          title={supportsLocal ? 'Queue a test scrape for review' : 'Test scrape requires local adapter'}
                        >
                          Test
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

    </div>
  );
};
